import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Row, Col, Card, Button, Form, Badge, Alert, Spinner, Tab, Tabs, InputGroup, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAtom } from 'jotai';
import { user_info } from 'jotai/UserJotaiInstance';
import { 
  faUser, 
  faHome, 
  faEdit, 
  faSave, 
  faTimes, 
  faMapMarkerAlt, 
  faCheckCircle, 
  faExclamationTriangle,
  faBrain,
  faRocket,
  faCalendar,
  faHandsHelping,
  faStar,
  faCamera,
  faThumbsUp,
  faClock,
  faArrowTrendUp,
  faArrowTrendDown,
  faEye,
  faInfoCircle,
  faChartBar,
  faRobot,
  faQuestionCircle,
  faDollarSign,
  faPaperPlane,
  faChartLine,
  faSearch,
  faMessage,
  faUsers,
  faHandshake,
  faSync,
  faCog,
  faSpinner,
  faArchive,
  faMoneyBillWave,
  faFire,
  faVideo,
  faBullseye,
  faMapMarkedAlt,
  faBullhorn,
  faFileLines,
  faLightbulb,
  faList
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UserInfo } from 'interfaces/user';
import AddressAutocompleteInput, { AddressSuggestion } from '../../common/AddressAutocompleteInput';

// Add custom CSS styles for purple color
const customStyles = `
  .text-purple {
    color: #9333ea !important;
  }
  .bg-purple {
    background-color: #9333ea !important;
  }
  .border-purple {
    border-color: #9333ea !important;
  }
`;

// Inject styles into document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = customStyles;
  document.head.appendChild(styleSheet);
}

interface SellerProfile {
  id: string;
  lead_id: string;
  lead_first_name: string;
  lead_last_name: string;
  lead_email: string;
  lead_phone: string;
  selling_timeframe: string;
  reason_for_selling: string;
  motivation_level: string;
  flexibility_level: string;
  property_address: string;
  property_city: string;
  property_state: string;
  property_zip: string;
  property_type: string;
  bedrooms: number;
  bathrooms: number;
  square_footage: number;
  lot_size_sqft: number;
  year_built: number;
  garage_spaces: number;
  pool: boolean;
  occupancy_status: string;
  property_condition: string;
  recent_improvements: boolean;
  improvements_description: string;
  improvements_cost: number;
  estimated_value: number;
  desired_price: number;
  minimum_price: number;
  pricing_strategy: string;
  seller_stage: string;
  is_also_buyer: boolean;
  buyer_preferences: any;
  ai_insights: any;
  seller_score: number;
  priority_level: string;
  created_on: string;
  updated_on: string;
  // Additional property metadata from MLS lookups
  apn?: string;
  subdivision?: string;
  ownership?: string;
  mls_data?: any;
}

interface SellerCMAReport {
  id: string;
  report_id: string;
  report_purpose: string;
  shared_with_seller: boolean;
  created_on: string;
  report_title?: string;
  predicted_value?: number;
  confidence_score?: number;
  pricing_recommendations?: {
    methodology: string;
    price_range: {
      low: number;
      high: number;
    };
    estimated_value: number;
    confidence_score: number;
    final_estimated_value: number;
  };
  marketing_recommendations?: {
    analysis_type: string;
    market_position: string;
    comparable_count: number;
    pricing_strategy: string;
    competitive_advantages: {
      highly_similar: number;
      market_saturation: string;
      similar_properties: number;
    };
  };
  timing_recommendations?: {
    market_trend: string;
    recommended_timing: string;
    market_insights: any;
    search_criteria: any;
  };
}

interface SellerMarketActivity {
  id: string;
  activity_type: string;
  activity_date: string;
  activity_description: string;
  attendee_count: number;
  feedback_score: number;
  follow_up_required: boolean;
}

interface SellerAIInteraction {
  id: string;
  interaction_type: string;
  interaction_date: string;
  user_query: string;
  ai_response: string;
  confidence_score: number;
  user_satisfaction: string;
  was_helpful: boolean;
}

interface Message {
  id: number;
  text: string;
  sender: "user" | "assistant";
  timestamp?: Date;
  type?: "text" | "insight" | "analysis";
  lastUpdated?: number;
}

interface SellerProfilePageProps {
  leadId: string;
  leadDetails: any;
}

const SellerProfilePage: React.FC<SellerProfilePageProps> = ({ leadId, leadDetails }) => {
  const [userInfo] = useAtom<UserInfo>(user_info);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('property-details');
  
  // Handle tab changes and load data as needed
  const handleTabChange = (tab: string) => {
    console.log('📑 [TAB CHANGE] Switching to tab:', tab);
    setActiveTab(tab);
    
    // Load data specific to the tab being accessed - ONLY if not already loaded
    switch (tab) {
      case 'ai-insights':
        console.log('🧠 [TAB CHANGE] Switching to AI insights tab');
        // Don't reload CMA reports - they're already loaded on page load
        break;
      case 'cma-reports':
        console.log('📊 [TAB CHANGE] Switching to CMA reports tab');
        // Don't reload CMA reports - they're already loaded on page load
        break;
      case 'market-activity':
        console.log('📈 [TAB CHANGE] Loading market activity for tab access');
        if (marketActivity.length === 0) {
        loadMarketActivity();
        }
        break;
      case 'ai-chat':
        console.log('🤖 [TAB CHANGE] Loading AI interactions for tab access');
        if (aiInteractions.length === 0) {
        loadAIInteractions();
        }
        break;
      default:
        // No additional loading needed for other tabs
        break;
    }
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  
  // Profile data
  const [sellerProfile, setSellerProfile] = useState<SellerProfile | null>(null);
  const [cmaReports, setCmaReports] = useState<SellerCMAReport[]>([]);
  const [marketActivity, setMarketActivity] = useState<SellerMarketActivity[]>([]);
  const [aiInteractions, setAiInteractions] = useState<SellerAIInteraction[]>([]);
  
  // Form data for editing
  const [formData, setFormData] = useState<Partial<SellerProfile>>({});
  
  // State for address and MLS lookup
const [addressValue, setAddressValue] = useState('');
  
  // Enhanced MLS lookup state (same as Step1PropertyInput)
  const [addressLocked, setAddressLocked] = useState(false);
  const [lookupStatus, setLookupStatus] = useState<'none' | 'loading' | 'success' | 'error'>('none');
  const [lookupProgress, setLookupProgress] = useState(0);
  const [lookupStep, setLookupStep] = useState('');

  // CMA Reports loading state to prevent excessive calls
  const [isLoadingCMAReports, setIsLoadingCMAReports] = useState(false);
  
  // Flag to prevent populateAllDataFromCMA from running multiple times
  const [hasPopulatedFromCMA, setHasPopulatedFromCMA] = useState(false);

  // Archive confirmation modal state
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [reportToArchive, setReportToArchive] = useState<string | null>(null);
  const [reportTitleToArchive, setReportTitleToArchive] = useState<string>('');

  // AI Chat state
  const [showProfileBuilder, setShowProfileBuilder] = useState(false);
  const [showEmbeddedChat, setShowEmbeddedChat] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Seller Metrics (using real CMA report data with proper marketStats extraction)
  const sellerMetrics = useMemo(() => {
    console.log('📊 [SELLER METRICS] Processing CMA reports:', {
      totalReports: cmaReports?.length || 0,
      reports: cmaReports?.map(r => ({
        id: r.id,
        created_on: r.created_on,
        estimated_value: r.pricing_recommendations?.estimated_value,
        final_estimated_value: r.pricing_recommendations?.final_estimated_value,
        has_marketStats: !!(r as any).marketStats
      }))
    });

    // Find report with estimated_value > 0 (skip empty reports)
    const selectedReport = cmaReports && cmaReports.length > 0 
      ? cmaReports.find(report => {
          const estimatedValue = Number(report.pricing_recommendations?.estimated_value) || 
                                Number(report.pricing_recommendations?.final_estimated_value) || 0;
          return estimatedValue > 0;
        })
      : null;

    // Extract comparable metrics from XGBoost CMA data or timing_recommendations
    let comparableMetrics = null;
    
    // First try to get data from timing_recommendations (old format)
    if (selectedReport?.timing_recommendations?.market_insights?.market_activity) {
      const marketActivity = selectedReport.timing_recommendations.market_insights.market_activity;
      const priceAnalysis = selectedReport.timing_recommendations.market_insights.price_analysis;
      
      comparableMetrics = {
        recent_sales_count: Number(marketActivity.recent_sales_6mo) || 0,
        average_sale_price: Number(priceAnalysis?.average_sale_price) || 0,
        median_sale_price: Number(priceAnalysis?.median_sale_price) || 0,
        average_days_on_market: Number(marketActivity.avg_days_on_market) || 0,
        price_trend: selectedReport.timing_recommendations.market_insights.trend_analysis?.price_trend || 'unknown'
      };
      
      console.log('📊 [COMPARABLE METRICS] Using legacy timing_recommendations format');
    }
    
    // If no legacy data, check for XGBoost CMA format with full comparable data
    if (!comparableMetrics && selectedReport) {
      // Check if this is XGBoost data with full_analysis field
      const fullAnalysis = (selectedReport as any).full_analysis;
      const cmaData = (selectedReport as any).cmaData;
      
      if (fullAnalysis?.comparable_properties?.comparables || cmaData?.comparable_properties?.comparables) {
        const comparables = fullAnalysis?.comparable_properties?.comparables || cmaData?.comparable_properties?.comparables || [];
        
        if (comparables.length > 0) {
          // Calculate metrics from actual comparable data
          const today = new Date();
          const sixMonthsAgo = new Date();
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          
          // For better data visibility, also calculate 12 months for the current test data
          const twelveMonthsAgo = new Date();
          twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
          
          console.log('📅 [DATE CALCULATION]', {
            today: today.toISOString(),
            todayFormatted: today.toLocaleDateString(),
            sixMonthsAgo: sixMonthsAgo.toISOString(),
            sixMonthsAgoFormatted: sixMonthsAgo.toLocaleDateString(),
            twelveMonthsAgo: twelveMonthsAgo.toISOString(),
            twelveMonthsAgoFormatted: twelveMonthsAgo.toLocaleDateString()
          });
          
          const recentSales = comparables.filter((comp: any) => {
            // Handle different date formats and ensure we have a valid date
            let saleDate;
            if (comp.close_date) {
              // Try parsing various date formats
              if (typeof comp.close_date === 'string') {
                saleDate = new Date(comp.close_date);
              } else if (comp.close_date instanceof Date) {
                saleDate = comp.close_date;
              } else {
                // Handle timestamp or other formats
                saleDate = new Date(comp.close_date);
              }
            } else if (comp.sale_date) {
              saleDate = new Date(comp.sale_date);
            } else if (comp.sold_date) {
              saleDate = new Date(comp.sold_date);
            } else {
              console.warn('⚠️ [DATE] No valid date field found for comparable:', comp.address || comp.id);
              return false; // No valid date found
            }
            
            // Check if date is valid and within 6 months
            const isValidDate = saleDate instanceof Date && !isNaN(saleDate.getTime());
            const today = new Date();
            const isRecent = isValidDate && saleDate >= sixMonthsAgo && saleDate <= today;
            
            if (isValidDate) {
              console.log('📅 [DATE CHECK]', {
                address: comp.address || 'Unknown',
                close_date: comp.close_date,
                parsed_date: saleDate.toISOString(),
                six_months_ago: sixMonthsAgo.toISOString(),
                today: today.toISOString(),
                is_future: saleDate > today,
                is_recent: isRecent
              });
            }
            
            return isRecent;
          });
          
          const avgPrice = comparables.reduce((sum: number, comp: any) => sum + (comp.close_price || 0), 0) / comparables.length;
          const avgDaysOnMarket = comparables.reduce((sum: number, comp: any) => sum + (comp.days_on_market || 0), 0) / comparables.length;
          
          // Calculate median price
          const prices = comparables.map((comp: any) => comp.close_price || 0).sort((a: number, b: number) => a - b);
          const medianPrice = prices.length > 0 ? 
            (prices.length % 2 === 0 ? 
              (prices[prices.length / 2 - 1] + prices[prices.length / 2]) / 2 : 
              prices[Math.floor(prices.length / 2)]) : 0;
          
          // Also calculate 12-month sales for better visibility with test data
          const recentSales12mo = comparables.filter((comp: any) => {
            let saleDate;
            if (comp.close_date) {
              saleDate = new Date(comp.close_date);
            } else if (comp.sale_date) {
              saleDate = new Date(comp.sale_date);
            } else if (comp.sold_date) {
              saleDate = new Date(comp.sold_date);
            } else {
              return false;
            }
            const isValidDate = saleDate instanceof Date && !isNaN(saleDate.getTime());
            return isValidDate && saleDate >= twelveMonthsAgo && saleDate <= today;
          });
          
          comparableMetrics = {
            recent_sales_count: recentSales.length,
            recent_sales_count_12mo: recentSales12mo.length,
            average_sale_price: Math.round(avgPrice),
            median_sale_price: Math.round(medianPrice),
            average_days_on_market: Math.round(avgDaysOnMarket),
            price_trend: 'stable' // Default for XGBoost data
          };
          
          console.log('📊 [COMPARABLE METRICS] Calculated from XGBoost comparable data:', {
            reportId: selectedReport.id,
            totalComparables: comparables.length,
            recentSales: recentSales.length,
            sampleComparable: comparables[0] ? {
              address: comparables[0].address,
              close_date: comparables[0].close_date,
              close_price: comparables[0].close_price,
              days_on_market: comparables[0].days_on_market,
              sale_date: comparables[0].sale_date,
              sold_date: comparables[0].sold_date
            } : 'No comparables',
            sixMonthsAgo: sixMonthsAgo.toISOString(),
            comparableMetrics: comparableMetrics,
            debugDates: comparables.slice(0, 5).map(comp => ({
              address: comp.address,
              close_date: comp.close_date,
              parsed: comp.close_date ? new Date(comp.close_date).toISOString() : 'invalid',
              isRecent: comp.close_date ? new Date(comp.close_date) >= sixMonthsAgo : false
            }))
          });
        }
      }
    }
    
    const pricingRecs = selectedReport?.pricing_recommendations;
    const marketingRecs = selectedReport?.marketing_recommendations;
    
    // Extract real values from CMA report - be explicit about data types
    const estimatedValue = Number(pricingRecs?.estimated_value) || Number(pricingRecs?.final_estimated_value) || 0;
    const confidenceScore = Number(pricingRecs?.confidence_score) || 0;
    const comparableCount = Number(marketingRecs?.comparable_count) || 0;
    
    const hasRealData = estimatedValue > 0 && selectedReport;
    const hasMarketStats = !!comparableMetrics;
    
    console.log('📊 [SELLER METRICS] Final selection:', {
      selectedReportId: selectedReport?.id,
      hasRealData,
      hasMarketStats,
      estimatedValue,
      confidenceScore,
      comparableCount,
      comparableMetrics
    });
    
    return {
      // Real estimated value from selected CMA report
      estimatedValue: estimatedValue,
      
      // Use real seller score from profile
      sellerScore: sellerProfile?.seller_score || 0,
      
      // Market position based on confidence score (0-1 to 0-100%)
      marketPosition: hasRealData ? Math.round(confidenceScore * 100) : 0,
      
      // AI confidence from CMA analysis
      aiConfidence: hasRealData ? Math.round(confidenceScore * 100) : 0,
      
      // Listing readiness based on data completeness and confidence
      listingReadiness: hasRealData ? 
        Math.min(100, Math.round(confidenceScore * 100)) : 0,
      
      // Additional CMA-specific metrics
      comparableCount: comparableCount,
      methodology: pricingRecs?.methodology || 'standard_cma',
      marketPositionType: marketingRecs?.market_position || 'unknown',
      priceRange: pricingRecs?.price_range || null,
      marketTrend: selectedReport?.timing_recommendations?.market_trend || 'unknown',
      
      // Comparable metrics from marketStats
      comparableMetrics: comparableMetrics,
      
      // Data source indicator
      dataSource: hasRealData ? 'cma_analysis' : 'no_data',
      
      // Report metadata (using selected report)
      latestReportDate: selectedReport?.created_on,
      reportPurpose: selectedReport?.report_purpose,
      latestReportId: selectedReport?.id
    };
  }, [cmaReports, sellerProfile?.seller_score]);

  useEffect(() => {
    console.log('🚀 useEffect triggered with leadId:', leadId);
    if (leadId) {
      const loadAllData = async () => {
        await loadSellerProfile();
        // OPTIMIZATION: Load property data from AI context for fast retrieval AFTER profile loads
        await loadPropertyDataFromAIContext();
        // Load CMA reports with await to ensure they're available for AI Insights
        await loadCMAReports();
        loadMarketActivity();
        loadAIInteractions();
      };
      loadAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  // Debug current state
  useEffect(() => {
    console.log('📊 Current state - loading:', loading, 'sellerProfile:', sellerProfile);
  }, [loading, sellerProfile]);



  // Initialize address value when profile loads
  useEffect(() => {
    if (sellerProfile?.property_address && !addressValue) {
      console.log('🏠 [ADDRESS INIT] Setting address value from profile:', sellerProfile.property_address);
      setAddressValue(sellerProfile.property_address);
    }
  }, [sellerProfile]);

  // CONSOLIDATED: Auto-populate ALL property data from CMA reports (address + property details + pricing)
  useEffect(() => {
    const populateAllDataFromCMA = async () => {
      if (sellerProfile && cmaReports.length > 0) {
        console.log('🔄 [CMA DATA SYNC] Starting comprehensive property data population from CMA reports...');
        
        // Find the first CMA report with a report_id
        const cmaReportWithData = cmaReports.find(report => report.report_id);
        
        if (!cmaReportWithData) {
          console.log('⚠️ [CMA DATA SYNC] No CMA reports with report_id found');
          return;
        }
        
        try {
          console.log('🔍 [CMA DATA SYNC] Fetching main report:', cmaReportWithData.report_id);
          const response = await axiosInstance.get(`reports/${cmaReportWithData.report_id}`, {
            headers: { 'X-User-Id': userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })() }
          });
          
          if (response.data.success && response.data.report?.propertyData) {
            // Parse propertyData (handle JSON string format)
            let propertyData = response.data.report.propertyData;
            if (typeof propertyData === 'string') {
              try {
                propertyData = JSON.parse(propertyData);
                console.log('✅ [CMA DATA SYNC] Parsed propertyData from JSON string:', propertyData);
              } catch (error) {
                console.error('❌ [CMA DATA SYNC] Failed to parse propertyData JSON:', error);
                return;
              }
            }
            
            // Build comprehensive update object
            const updates: Partial<SellerProfile> = {};
            let hasUpdates = false;
            
            // 1. ADDRESS DATA - Always update if missing
            if (propertyData.address && !sellerProfile.property_address) {
              updates.property_address = propertyData.address;
              setAddressValue(propertyData.address);
              hasUpdates = true;
              console.log('📍 [CMA DATA SYNC] Adding address:', propertyData.address);
            }
            
            if (propertyData.city && !sellerProfile.property_city) {
              updates.property_city = propertyData.city;
              hasUpdates = true;
            }
            
            if (propertyData.state && !sellerProfile.property_state) {
              updates.property_state = propertyData.state;
              hasUpdates = true;
            }
            
            if (propertyData.zip && !sellerProfile.property_zip) {
              updates.property_zip = propertyData.zip;
              hasUpdates = true;
            }
            
            // 2. PROPERTY CHARACTERISTICS - Update if missing or zero
            if (propertyData.bedrooms && (!sellerProfile.bedrooms || sellerProfile.bedrooms === 0)) {
              updates.bedrooms = propertyData.bedrooms;
              hasUpdates = true;
              console.log('🛏️ [CMA DATA SYNC] Adding bedrooms:', propertyData.bedrooms);
            }
            
            if (propertyData.bathrooms && (!sellerProfile.bathrooms || sellerProfile.bathrooms === 0)) {
              updates.bathrooms = propertyData.bathrooms;
              hasUpdates = true;
              console.log('🚿 [CMA DATA SYNC] Adding bathrooms:', propertyData.bathrooms);
            }
            
            if (propertyData.square_footage && (!sellerProfile.square_footage || sellerProfile.square_footage === 0)) {
              updates.square_footage = propertyData.square_footage;
              hasUpdates = true;
              console.log('📐 [CMA DATA SYNC] Adding square footage:', propertyData.square_footage);
            }
            
            if (propertyData.year_built && (!sellerProfile.year_built || sellerProfile.year_built === 0)) {
              updates.year_built = propertyData.year_built;
              hasUpdates = true;
              console.log('🏗️ [CMA DATA SYNC] Adding year built:', propertyData.year_built);
            }
            
            if (propertyData.garage_spaces && (!sellerProfile.garage_spaces || sellerProfile.garage_spaces === 0)) {
              updates.garage_spaces = propertyData.garage_spaces;
              hasUpdates = true;
              console.log('🚗 [CMA DATA SYNC] Adding garage spaces:', propertyData.garage_spaces);
            }
            
            if (propertyData.lot_size_sqft && (!sellerProfile.lot_size_sqft || sellerProfile.lot_size_sqft === 0)) {
              updates.lot_size_sqft = propertyData.lot_size_sqft;
              hasUpdates = true;
              console.log('🏞️ [CMA DATA SYNC] Adding lot size:', propertyData.lot_size_sqft);
            }
            
            if (propertyData.property_type && (!sellerProfile.property_type || sellerProfile.property_type === 'single_family')) {
              updates.property_type = propertyData.property_type;
              hasUpdates = true;
              console.log('🏠 [CMA DATA SYNC] Adding property type:', propertyData.property_type);
            }
            
            // 3. PRICING DATA - Update from CMA pricing recommendations
            const pricingData = cmaReportWithData.pricing_recommendations;
            if (pricingData) {
              const cmaEstimatedValue = pricingData.estimated_value || pricingData.final_estimated_value || 0;
              
              if (cmaEstimatedValue > 0 && (!sellerProfile.estimated_value || sellerProfile.estimated_value === 0)) {
                updates.estimated_value = cmaEstimatedValue;
                updates.desired_price = Math.round(cmaEstimatedValue * 1.02);
                updates.minimum_price = Math.round(cmaEstimatedValue * 0.95);
                updates.pricing_strategy = 'market_value';
                updates.seller_score = Math.max(sellerProfile.seller_score || 0, 75);
                hasUpdates = true;
                console.log('💰 [CMA DATA SYNC] Adding pricing data:', cmaEstimatedValue);
              }
            }
            
            // Apply all updates at once
            if (hasUpdates) {
              console.log('🔄 [CMA DATA SYNC] Applying comprehensive updates:', updates);
              
              const updatedProfile = { ...sellerProfile, ...updates };
              setSellerProfile(updatedProfile);
              setFormData(updatedProfile);
              
              // Save to database
              try {
                await axiosInstance.put(`sellers/${leadId}/profile`, 
                  mapFormDataToDatabase(updatedProfile), {
                  headers: { 'X-User-Id': userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })() }
                });
                console.log('✅ [CMA DATA SYNC] All property data saved successfully');
              } catch (error) {
                console.error('❌ [CMA DATA SYNC] Failed to save property data:', error);
              }
            } else {
              console.log('ℹ️ [CMA DATA SYNC] No updates needed - all data already present');
            }
          }
        } catch (error) {
          console.error('❌ [CMA DATA SYNC] Error fetching main report:', error);
        }
      }
    };
    
    // Only run once when both sellerProfile and cmaReports are available
    if (sellerProfile && cmaReports.length > 0 && !hasPopulatedFromCMA) {
      populateAllDataFromCMA();
      setHasPopulatedFromCMA(true);
    }
  }, [sellerProfile, cmaReports, leadId, userInfo?.id, hasPopulatedFromCMA]);

  // Ensure workflow template is assigned for seller profile
  const ensureSellerWorkflowAssignment = async (profileId: string, leadId: string) => {
    try {
      console.log('🔄 Ensuring seller workflow assignment for profile:', profileId);
      
      const response = await axiosInstance.post('/profiles/ensure-workflow', {
        profile_type: 'seller',
        profile_id: profileId,
        lead_id: leadId,
        user_id: userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })()
      });
      
      if (response.data.status === 'success') {
        const workflowInfo = response.data.workflow_info;
        console.log('✅ Seller workflow assignment ensured:', workflowInfo);
        
        // Update UI to show workflow status
        if (workflowInfo.status === 'created') {
          toast.success('AI-guided profile building workflow activated!');
        }
        
        return workflowInfo;
      } else {
        console.warn('⚠️ Failed to ensure seller workflow assignment:', response.data.error);
      }
    } catch (error: any) {
      console.error('❌ Error ensuring seller workflow assignment:', error);
      // Don't show error toast as this is not critical for basic functionality
    }
  };

  const loadSellerProfile = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading seller profile for leadId:', leadId);
      
      // Try to load real profile from API first
      console.log('🔍 Attempting to load real seller profile from API...');
      
      // Get seller profile from real API
      const response = await axiosInstance.get(`sellers/${leadId}/profile`, {
        params: { user_id: userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })() }
      });
      
      console.log('📊 API Response:', response.data);
      
      if (response.data.status === 'success') {
        const profile = response.data.seller_profile;
        console.log('✅ Profile loaded successfully:', profile);
        setSellerProfile(profile);
        setFormData(profile);
        
        // Ensure workflow template is assigned for this seller profile
        await ensureSellerWorkflowAssignment(profile.id, leadId);
      } else {
        // If no profile exists, create one with lead details
        const defaultProfile: Partial<SellerProfile> = {
          lead_first_name: leadDetails?.lead_first_name || 'Unknown',
          lead_last_name: leadDetails?.lead_last_name || 'Lead',
          lead_email: leadDetails?.lead_email || '',
          lead_phone: leadDetails?.lead_phone || '',
          selling_timeframe: '',
          reason_for_selling: '',
          motivation_level: 'medium',
          flexibility_level: 'moderate',
          property_address: '',
          property_city: '',
          property_state: '',
          property_zip: '',
          property_type: 'single_family',
          bedrooms: 0,
          bathrooms: 0,
          square_footage: 0,
          lot_size_sqft: 0,
          year_built: 0,
          garage_spaces: 0,
          pool: false,
          occupancy_status: 'owner_occupied',
          property_condition: 'good',
          recent_improvements: false,
          improvements_description: '',
          improvements_cost: 0,
          estimated_value: 0,
          desired_price: 0,
          minimum_price: 0,
          pricing_strategy: 'market_value',
          seller_stage: 'initial_contact',
          is_also_buyer: false,
          buyer_preferences: {},
          ai_insights: {},
          seller_score: 0,
          priority_level: 'medium',
          created_on: new Date().toISOString(),
          updated_on: new Date().toISOString()
        };
        
        console.log('📋 Using default profile:', defaultProfile);
        setSellerProfile(defaultProfile as SellerProfile);
        setFormData(defaultProfile);
        
        // For new profiles, we'll assign workflow after profile creation
      }
    } catch (error: any) {
      console.error("❌ Error loading seller profile:", error);
      
      // Check if this is a 404 (profile doesn't exist) vs other errors
      const isProfileNotFound = error.response?.status === 404;
      
      if (isProfileNotFound) {
        console.log("📋 No seller profile found - this is normal for new leads");
        // Don't show error toast for missing profiles - this is expected
      } else {
        // Only show error for actual API/network issues
        console.error("🚨 Actual API error:", error.response?.status, error.message);
        toast.error("Failed to load seller profile - please check your connection");
      }
      
      // Create a basic profile using lead data to prevent infinite loading
      const fallbackProfile: Partial<SellerProfile> = {
        id: `new_seller_${leadId}`,
        lead_id: leadId,
        lead_first_name: leadDetails?.lead_first_name || 'Unknown',
        lead_last_name: leadDetails?.lead_last_name || 'Lead',
        lead_email: leadDetails?.lead_email || '',
        lead_phone: leadDetails?.lead_phone || '',
        property_address: '',
        property_city: '',
        property_state: 'AZ',
        property_zip: '',
        property_type: 'single_family',
        bedrooms: 0,
        bathrooms: 0,
        square_footage: 0,
        lot_size_sqft: 0,
        year_built: 0,
        garage_spaces: 0,
        pool: false,
        selling_timeframe: 'not_specified',
        reason_for_selling: '',
        motivation_level: 'medium',
        flexibility_level: 'somewhat_flexible',
        occupancy_status: 'owner_occupied',
        property_condition: 'good',
        recent_improvements: false,
        improvements_description: '',
        improvements_cost: 0,
        estimated_value: 0,
        desired_price: 0,
        minimum_price: 0,
        pricing_strategy: 'market_value',
        seller_stage: 'initial_contact',
        is_also_buyer: false,
        buyer_preferences: {},
        ai_insights: {},
        seller_score: 0,
        priority_level: 'medium',
        created_on: new Date().toISOString(),
        updated_on: new Date().toISOString()
      };
      
      console.log('🔧 Setting fallback profile for new seller:', fallbackProfile);
      setSellerProfile(fallbackProfile as SellerProfile);
      setFormData(fallbackProfile);
    } finally {
      console.log('🏁 Profile loading finished, setting loading to false');
      setLoading(false);
    }
  };

  const loadCMAReports = async () => {
    // Prevent multiple simultaneous calls
    if (isLoadingCMAReports) {
      console.log('📊 [CMA REPORTS] Already loading, skipping duplicate call');
      return;
    }

    try {
      setIsLoadingCMAReports(true);
      console.log('📊 [CMA REPORTS] Loading CMA reports for lead:', leadId);
      
      // Load CMA reports from correct seller API endpoint
      const response = await axiosInstance.get(`sellers/${leadId}/cma-reports`, {
        headers: {
          'X-User-Id': userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })()
        }
      });
      
      console.log('📊 [CMA REPORTS] Raw API response:', response.data);
      
      if (response.data.status === 'success') {
        const reports = response.data.reports || [];
        console.log(`✅ [CMA REPORTS] Successfully loaded ${reports.length} CMA reports`);
        
        // Log first report details for debugging
        if (reports.length > 0) {
          const firstReport = reports[0];
          console.log('📋 [CMA REPORTS] First report details:', {
            id: firstReport.id,
            estimated_value: firstReport.pricing_recommendations?.estimated_value || firstReport.predicted_value,
            comparable_count: firstReport.marketing_recommendations?.comparable_count,
            confidence: firstReport.pricing_recommendations?.confidence_score,
            created_on: firstReport.created_on
          });
        }
        
        setCmaReports(reports);
        
        // Show success toast if we have reports (but only if more than 5 to avoid spam)
        if (reports.length > 5) {
          console.warn(`⚠️ [CMA REPORTS] Unusually high number of reports (${reports.length}) - possible duplicate generation bug`);
        }
      } else {
        console.warn('⚠️ [CMA REPORTS] API returned non-success status:', response.data);
        setCmaReports([]);
      }
    } catch (error: any) {
      console.error("❌ [CMA REPORTS] Error loading CMA reports:", error);
      
      // Show specific error messages for debugging
      if (error.response?.status === 404) {
        console.warn('⚠️ [CMA REPORTS] Seller profile not found - this is expected for new profiles');
      } else if (error.response?.status === 500) {
        console.error('🚨 [CMA REPORTS] Server error:', error.response?.data);
        toast.error('Server error loading CMA reports. Please try refreshing.');
      } else {
        console.error('🚨 [CMA REPORTS] Network or other error:', error.message);
        toast.error('Failed to load CMA reports. Please check your connection.');
      }
      
      setCmaReports([]);
    } finally {
      setIsLoadingCMAReports(false);
    }
  };

  const handleArchiveClick = (reportId: string, reportTitle?: string) => {
    setReportToArchive(reportId);
    setReportTitleToArchive(reportTitle || 'this CMA report');
    setShowArchiveModal(true);
  };

  const confirmArchive = async () => {
    if (!reportToArchive) return;
    
    try {
      console.log('📦 [ARCHIVE] Archiving CMA report:', reportToArchive);
      
      const response = await axiosInstance.patch(`sellers/${leadId}/cma-reports/${reportToArchive}/archive`, {}, {
        headers: {
          'X-User-Id': userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })()
        }
      });
      
      if (response.data.status === 'success') {
        toast.success('CMA report archived successfully');
        // Refresh the CMA reports list to remove the archived report
        loadCMAReports();
        setShowArchiveModal(false);
        setReportToArchive(null);
        setReportTitleToArchive('');
      } else {
        throw new Error(response.data.error || 'Failed to archive report');
      }
    } catch (error: any) {
      console.error('❌ [ARCHIVE] Failed to archive CMA report:', error);
      toast.error(error.response?.data?.error || 'Failed to archive report');
    }
  };

  const cancelArchive = () => {
    setShowArchiveModal(false);
    setReportToArchive(null);
    setReportTitleToArchive('');
  };

  const loadMarketActivity = async () => {
    try {
      // Real API call to get market activity
      const response = await axiosInstance.get(`sellers/${leadId}/market-activity`, {
        headers: {
          'X-User-Id': userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })()
        }
      });
      
      if (response.data.status === 'success') {
        setMarketActivity(response.data.market_activity || []);
      } else {
        console.warn('No market activity found for seller');
        setMarketActivity([]);
      }
    } catch (error: any) {
      console.error("Error loading market activity:", error);
      // Set empty array on error instead of mock data
      setMarketActivity([]);
      
      // Only show error toast for actual API failures, not 404s
      if (error.response?.status !== 404) {
        toast.error('Failed to load market activity');
      }
    }
  };

  const loadAIInteractions = async () => {
    try {
      // Real API call to get AI interactions
      const response = await axiosInstance.get(`sellers/${leadId}/ai-interactions`, {
        headers: {
          'X-User-Id': userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })()
        }
      });
      
      if (response.data.status === 'success') {
        setAiInteractions(response.data.ai_interactions || []);
      } else {
        console.warn('No AI interactions found for seller');
        setAiInteractions([]);
      }
    } catch (error: any) {
      console.error("Error loading AI interactions:", error);
      // Set empty array on error instead of mock data
      setAiInteractions([]);
      
      // Only show error toast for actual API failures, not 404s
      if (error.response?.status !== 404) {
        toast.error('Failed to load AI interactions');
      }
    }
  };

  // OPTIMIZATION: Load property data from lead_ai_context for fast UI retrieval
  const loadPropertyDataFromAIContext = async () => {
    try {
      console.log('🧠 [AI CONTEXT] Loading comprehensive seller data from lead_ai_context...');
      
      const response = await axiosInstance.get(`leads/ai-context/${leadId}`, {
        headers: {
          'X-User-Id': userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })()
        }
      });
      
      if (response.data.status === 'success' && response.data.ai_context) {
        const aiContext = response.data.ai_context;
        console.log('✅ [AI CONTEXT] AI context loaded:', aiContext);
        
        // Initialize updated form data with current formData as base
        let updatedFormData = { ...formData };
        let hasPropertyData = false;
        let hasMarketData = false;
        let hasSellerData = false;
        
        // ====================================
        // 1. LOAD PROPERTY DETAILS FROM SELLER CONTEXT
        // ====================================
        // Check both the new structure (property_analysis.property_details) and old structure (property_details)
        let propertyData = null;
        if (aiContext.seller_context?.property_analysis?.property_details) {
          propertyData = {
            ...aiContext.seller_context.property_analysis.property_details,
            address: aiContext.seller_context.property_analysis.address,
            coordinates: aiContext.seller_context.property_analysis.coordinates
          };
          console.log('🏠 [AI CONTEXT] Property details found in seller_context.property_analysis:', propertyData);
        } else if (aiContext.seller_context?.property_details) {
          propertyData = aiContext.seller_context.property_details;
          console.log('🏠 [AI CONTEXT] Property details found in seller_context.property_details (legacy):', propertyData);
        }
        
        if (propertyData) {
          
          updatedFormData = {
            ...updatedFormData,
            // Address information
            property_address: propertyData.address || updatedFormData.property_address || '',
            property_city: propertyData.city || updatedFormData.property_city || '',
            property_state: propertyData.state || updatedFormData.property_state || 'AZ',
            property_zip: propertyData.zip || propertyData.postal_code || updatedFormData.property_zip || '',
            
            // Property characteristics
            property_type: propertyData.property_type || updatedFormData.property_type || 'single_family',
            bedrooms: propertyData.bedrooms || updatedFormData.bedrooms || 0,
            bathrooms: propertyData.bathrooms || updatedFormData.bathrooms || 0,
            square_footage: propertyData.square_footage || propertyData.living_area || updatedFormData.square_footage || 0,
            lot_size_sqft: propertyData.lot_size || propertyData.lot_size_sqft || updatedFormData.lot_size_sqft || 0,
            year_built: propertyData.year_built || updatedFormData.year_built || 0,
            garage_spaces: propertyData.garage_spaces !== undefined ? propertyData.garage_spaces : updatedFormData.garage_spaces || 0,
            pool: propertyData.pool !== undefined ? propertyData.pool : updatedFormData.pool || false,
            
            // Property condition and features
            property_condition: propertyData.condition || updatedFormData.property_condition || 'good',
            occupancy_status: propertyData.occupancy_status || updatedFormData.occupancy_status || 'owner_occupied',
            
            // Property identifiers and metadata
            apn: propertyData.apn || updatedFormData.apn || '',
            subdivision: propertyData.subdivision || updatedFormData.subdivision || '',
            ownership: propertyData.ownership || updatedFormData.ownership || '',
            
            // Valuation data
            estimated_value: propertyData.estimated_value || propertyData.list_price || propertyData.last_sale_price || updatedFormData.estimated_value || 0,
            
            // MLS data reference
            mls_data: propertyData.mls_data || updatedFormData.mls_data || null
          };
          
          // Set address value for the address input field
          if (propertyData.address) {
            setAddressValue(propertyData.address);
            setAddressLocked(true); // Lock address since we have verified MLS data
          }
          
          hasPropertyData = true;
          console.log('✅ [AI CONTEXT] Property details loaded from cache');
        }
        
        // ====================================
        // 2. LOAD MARKET ANALYSIS DATA
        // ====================================
        if (aiContext.seller_context?.market_analysis) {
          const marketData = aiContext.seller_context.market_analysis;
          console.log('📊 [AI CONTEXT] Market analysis found in seller_context:', marketData);
          
          // Also get market valuation data if available
          const marketValuation = aiContext.seller_context?.market_valuation;
          if (marketValuation) {
            console.log('💰 [AI CONTEXT] Market valuation found:', marketValuation);
          }
          
          // Update AI insights with market data and valuation
          updatedFormData = {
            ...updatedFormData,
            ai_insights: {
              ...updatedFormData.ai_insights,
              market_analysis: marketData,
              market_valuation: marketValuation,
              report_id: marketData.report_id,
              confidence_score: marketValuation?.confidence_score || marketData.confidence_score,
              data_sources: marketData.data_sources,
              last_updated: marketData.last_updated || new Date().toISOString(),
              // Add pricing recommendations from market data
              pricing_recommendation: marketValuation?.pricing_recommendation || `List at $${Math.round((marketValuation?.estimated_value || 0) * 1.02).toLocaleString()} for optimal market position`,
              timing_advice: marketData.timing_advice || 'Spring market provides 8% higher selling prices'
            }
          };
          
          // Update estimated value from market valuation - ALWAYS update if available
          if (marketValuation?.estimated_value) {
            updatedFormData.estimated_value = marketValuation.estimated_value;
            console.log('💰 [AI CONTEXT] Updated estimated value from market valuation:', marketValuation.estimated_value);
          }
          
          // Update seller score from confidence score if available
          if (marketValuation?.confidence_score) {
            updatedFormData.seller_score = Math.round(marketValuation.confidence_score * 100);
            console.log('📊 [AI CONTEXT] Updated seller score from confidence:', updatedFormData.seller_score);
          }
          
          // Update pricing strategy from market data
          if (marketValuation?.estimated_value) {
            const estimatedValue = marketValuation.estimated_value;
            updatedFormData.desired_price = Math.round(estimatedValue * 1.02); // List 2% above estimate
            updatedFormData.minimum_price = Math.round(estimatedValue * 0.95); // Minimum 5% below estimate
            console.log('💰 [AI CONTEXT] Updated pricing strategy - Desired:', updatedFormData.desired_price, 'Minimum:', updatedFormData.minimum_price);
          }
          
          hasMarketData = true;
          console.log('✅ [AI CONTEXT] Market analysis and valuation loaded from cache');
        }
        
        // ====================================
        // 3. LOAD SELLER PREFERENCES & CONTEXT
        // ====================================
        if (aiContext.seller_context?.seller_preferences) {
          const sellerPrefs = aiContext.seller_context.seller_preferences;
          console.log('👤 [AI CONTEXT] Seller preferences found in seller_context:', sellerPrefs);
          
          updatedFormData = {
            ...updatedFormData,
            selling_timeframe: sellerPrefs.selling_timeframe || updatedFormData.selling_timeframe || '',
            reason_for_selling: sellerPrefs.reason_for_selling || updatedFormData.reason_for_selling || '',
            motivation_level: sellerPrefs.motivation_level || updatedFormData.motivation_level || 'medium',
            flexibility_level: sellerPrefs.flexibility_level || updatedFormData.flexibility_level || 'moderate',
            seller_stage: sellerPrefs.seller_stage || updatedFormData.seller_stage || 'initial_contact',
            is_also_buyer: sellerPrefs.is_also_buyer !== undefined ? sellerPrefs.is_also_buyer : updatedFormData.is_also_buyer || false
          };
          
          hasSellerData = true;
          console.log('✅ [AI CONTEXT] Seller preferences loaded from cache');
        }
        
        // ====================================
        // 4. LOAD PRICING STRATEGY DATA
        // ====================================
        if (aiContext.seller_context?.pricing_strategy) {
          const pricingData = aiContext.seller_context.pricing_strategy;
          console.log('💰 [AI CONTEXT] Pricing strategy found in seller_context:', pricingData);
          
          updatedFormData = {
            ...updatedFormData,
            desired_price: pricingData.desired_price || updatedFormData.desired_price || 0,
            minimum_price: pricingData.minimum_price || updatedFormData.minimum_price || 0,
            pricing_strategy: pricingData.strategy || pricingData.pricing_strategy || updatedFormData.pricing_strategy || 'market_value'
          };
          
          console.log('✅ [AI CONTEXT] Pricing strategy loaded from cache');
        }
        
        // ====================================
        // 5. LOAD PROPERTY IMPROVEMENTS DATA
        // ====================================
        if (aiContext.seller_context?.property_improvements) {
          const improvementsData = aiContext.seller_context.property_improvements;
          console.log('🔨 [AI CONTEXT] Property improvements found in seller_context:', improvementsData);
          
          updatedFormData = {
            ...updatedFormData,
            recent_improvements: improvementsData.has_improvements !== undefined ? improvementsData.has_improvements : updatedFormData.recent_improvements || false,
            improvements_description: improvementsData.description || updatedFormData.improvements_description || '',
            improvements_cost: improvementsData.cost || updatedFormData.improvements_cost || 0
          };
          
          console.log('✅ [AI CONTEXT] Property improvements loaded from cache');
        }
        
        // ====================================
        // 6. APPLY ALL UPDATES TO FORM DATA
        // ====================================
        if (hasPropertyData || hasMarketData || hasSellerData) {
          console.log('📝 [AI CONTEXT] Applying comprehensive cached data to form...');
          console.log('📊 [AI CONTEXT] Updated form data summary:', {
            property_address: updatedFormData.property_address,
            bedrooms: updatedFormData.bedrooms,
            bathrooms: updatedFormData.bathrooms,
            square_footage: updatedFormData.square_footage,
            estimated_value: updatedFormData.estimated_value,
            selling_timeframe: updatedFormData.selling_timeframe,
            motivation_level: updatedFormData.motivation_level
          });
          
          // Update both form data and seller profile state
          setFormData(updatedFormData);
          
          // Update seller profile state with any available data (not just when we have both property and seller data)
          setSellerProfile(prevProfile => ({
            ...prevProfile,
            ...updatedFormData
          } as SellerProfile));
          
          console.log('📊 [AI CONTEXT] Updated seller profile state with:', {
            estimated_value: updatedFormData.estimated_value,
            seller_score: updatedFormData.seller_score,
            desired_price: updatedFormData.desired_price,
            minimum_price: updatedFormData.minimum_price
          });
          
          // Show success notification
          const loadedDataTypes = [];
          if (hasPropertyData) loadedDataTypes.push('Property Data');
          if (hasMarketData) loadedDataTypes.push('Market Analysis');
          if (hasSellerData) loadedDataTypes.push('Seller Preferences');
          
          toast.success(`⚡ ${loadedDataTypes.join(', ')} loaded from cache`, {
            duration: 4000,
            icon: '⚡'
          });
          
          console.log('🎉 [AI CONTEXT] All cached data successfully applied to form');
          
          return {
            propertyData: hasPropertyData ? aiContext.seller_context.property_details : null,
            marketData: hasMarketData ? aiContext.seller_context.market_analysis : null,
            sellerData: hasSellerData ? aiContext.seller_context.seller_preferences : null,
            loadedDataTypes
          };
        }
        
      } else {
        console.log('ℹ️ [AI CONTEXT] No AI context found for this lead yet');
      }
      
    } catch (error: any) {
      console.error('❌ [AI CONTEXT] Error loading AI context:', error);
      
      // Don't show error toast for 404s (no context exists yet)
      if (error.response?.status !== 404) {
        console.error('🚨 [AI CONTEXT] Actual API error:', error.response?.status, error.message);
      }
    }
    
    return null;
  };

  const saveSellerProfile = async () => {
    try {
      setSaving(true);
      
      // Map frontend enum values to database-compliant values
      const mappedFormData = mapFormDataToDatabase(formData);
      
      console.log('💾 Saving seller profile with formData:', formData);
      console.log('🔄 Mapped to database format:', mappedFormData);
      console.log('📋 Request details:', {
        url: `/api/sellers/${leadId}/profile`,
        method: 'PUT',
        headers: { 'X-User-Id': userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })() },
        data: mappedFormData
      });
      
      // Update seller profile via API with mapped data
      const response = await axiosInstance.put(`sellers/${leadId}/profile`, mappedFormData, {
        headers: { 'X-User-Id': userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })() }
      });
      
      console.log('📨 API Response:', response.data);
      
      if (response.data.status === 'success') {
        setSellerProfile(response.data.seller_profile);
        setFormData(response.data.seller_profile); // Update form data to match saved profile
        setEditing(false);
        toast.success("Seller profile updated successfully!");
        console.log('✅ Profile saved successfully');
      } else {
        console.error('❌ API returned error status:', response.data);
        throw new Error(response.data.error || 'Failed to update profile');
      }
    } catch (error: any) {
      console.error("❌ Error saving seller profile:", error);
      
      // Log detailed error information
      if (error.response) {
        console.error('📋 Error response data:', error.response.data);
        console.error('📋 Error response status:', error.response.status);
        console.error('📋 Error response headers:', error.response.headers);
        
        // Show specific error message from API
        const errorMessage = error.response.data?.error || error.response.data?.message || 'Failed to save seller profile';
        toast.error(`Save failed: ${errorMessage}`);
      } else if (error.request) {
        console.error('📋 No response received:', error.request);
        toast.error("No response from server - check your connection");
      } else {
        console.error('📋 Request setup error:', error.message);
        toast.error(`Request error: ${error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  // Enum mapping functions to convert frontend values to database-compliant values
  const mapFormDataToDatabase = (formData: Partial<SellerProfile>) => {
    return {
      ...formData,
      selling_timeframe: mapSellingTimeframe(formData.selling_timeframe),
      motivation_level: mapMotivationLevel(formData.motivation_level),
      flexibility_level: mapFlexibilityLevel(formData.flexibility_level),
      property_type: mapPropertyType(formData.property_type),
      occupancy_status: mapOccupancyStatus(formData.occupancy_status),
      property_condition: mapPropertyCondition(formData.property_condition),
      pricing_strategy: mapPricingStrategy(formData.pricing_strategy),
      seller_stage: mapSellerStage(formData.seller_stage),
      priority_level: mapPriorityLevel(formData.priority_level)
    };
  };

  const mapSellingTimeframe = (value?: string): string => {
    // Map frontend values to valid database enum values
    const mapping = {
      'within_1_month': 'within_1_month',
      'within_3_months': 'within_3_months',
      'within_6_months': 'within_6_months',
      'within_1_year': 'within_1_year',
      'flexible': 'flexible',
      'not_specified': 'not_specified'
    };
    return mapping[value as keyof typeof mapping] || 'not_specified';
  };

  const mapMotivationLevel = (value?: string): string => {
    // Map frontend values to valid database enum values
    const mapping = {
      'urgent': 'urgent',
      'high': 'high',
      'medium': 'medium',
      'low': 'low'
    };
    return mapping[value as keyof typeof mapping] || 'medium';
  };

  const mapFlexibilityLevel = (value?: string): string => {
    // Map frontend values to valid database enum values
    const mapping = {
      'rigid': 'rigid',
      'moderate': 'moderate',
      'flexible': 'flexible',
      'very_flexible': 'very_flexible'
    };
    return mapping[value as keyof typeof mapping] || 'moderate';
  };

  const mapPropertyType = (value?: string): string => {
    // Map frontend values to valid database enum values
    const mapping = {
      'single_family': 'single_family',
      'townhouse': 'townhouse',
      'condo': 'condo',
      'multi_family': 'multi_family',
      'land': 'land',
      'commercial': 'commercial',
      'manufactured': 'manufactured'
    };
    return mapping[value as keyof typeof mapping] || 'single_family';
  };

  const mapOccupancyStatus = (value?: string): string => {
    // Map frontend values to valid database enum values
    const mapping = {
      'owner_occupied': 'owner_occupied',
      'tenant_occupied': 'tenant_occupied',
      'vacant': 'vacant'
    };
    return mapping[value as keyof typeof mapping] || 'owner_occupied';
  };

  const mapPropertyCondition = (value?: string): string => {
    // Map frontend values to valid database enum values
    const mapping = {
      'excellent': 'excellent',
      'good': 'good',
      'fair': 'fair',
      'needs_work': 'needs_work',
      'fixer_upper': 'fixer_upper'
    };
    return mapping[value as keyof typeof mapping] || 'good';
  };

  const mapPricingStrategy = (value?: string): string => {
    // Map frontend values to valid database enum values
    const mapping = {
      'aggressive': 'aggressive',
      'market_value': 'market_value',
      'premium': 'premium',
      'flexible': 'flexible',
      'not_specified': 'not_specified'
    };
    return mapping[value as keyof typeof mapping] || 'market_value';
  };

  const mapSellerStage = (value?: string): string => {
    // Map frontend values to valid database enum values
    const mapping = {
      'initial_contact': 'considering',
      'considering': 'considering',
      'researching': 'researching',
      'preparing': 'preparing',
      'ready_to_list': 'ready_to_list',
      'listing': 'listing',
      'showing': 'showing',
      'reviewing_offers': 'reviewing_offers',
      'under_contract': 'under_contract',
      'closed': 'closed',
      'paused': 'paused',
      'lost': 'lost'
    };
    return mapping[value as keyof typeof mapping] || 'considering';
  };

  const mapPriorityLevel = (value?: string): string => {
    // Map frontend values to valid database enum values
    const mapping = {
      'urgent': 'urgent',
      'high': 'high',
      'medium': 'medium',
      'low': 'low'
    };
    return mapping[value as keyof typeof mapping] || 'medium';
  };



  // Simplified address change handler for AddressAutocompleteInput
  const handleAddressChange = (value: string) => {
    console.log('🔍 Address input changed:', value);
    setAddressValue(value);
  };

  // Enhanced address selection handler with integrated CMA endpoint
  const handleAddressSelect = async (suggestion: AddressSuggestion) => {
    console.log('🏠 Address selected:', suggestion);
    
    // Set the address value - AddressAutocompleteInput handles clearing its own suggestions
    setAddressValue(suggestion.address);
    
    // Enhanced address parsing with validation
    const parseAddressComponents = (fullAddress: string) => {
      try {
        console.log('🔍 Parsing address:', fullAddress);

        const stateAbbrevs: Record<string, string> = {
          'alabama':'AL','alaska':'AK','arizona':'AZ','arkansas':'AR','california':'CA','colorado':'CO','connecticut':'CT','delaware':'DE','florida':'FL','georgia':'GA','hawaii':'HI','idaho':'ID','illinois':'IL','indiana':'IN','iowa':'IA','kansas':'KS','kentucky':'KY','louisiana':'LA','maine':'ME','maryland':'MD','massachusetts':'MA','michigan':'MI','minnesota':'MN','mississippi':'MS','missouri':'MO','montana':'MT','nebraska':'NE','nevada':'NV','new hampshire':'NH','new jersey':'NJ','new mexico':'NM','new york':'NY','north carolina':'NC','north dakota':'ND','ohio':'OH','oklahoma':'OK','oregon':'OR','pennsylvania':'PA','rhode island':'RI','south carolina':'SC','south dakota':'SD','tennessee':'TN','texas':'TX','utah':'UT','vermont':'VT','virginia':'VA','washington':'WA','west virginia':'WV','wisconsin':'WI','wyoming':'WY'
        };

        const parts = fullAddress.split(',').map(part => part.trim());
        console.log('📍 Address parts:', parts);

        if (parts.length >= 2) {
          const streetAddress = parts[0];
          const city = parts.length >= 3 ? parts[1] : '';

          // Search ALL parts for state/zip (handles "Arizona 85281" or "AZ 85281")
          let state = '';
          let zip = '';
          for (const part of parts) {
            // Try "AZ 85281" format
            const abbrevMatch = part.match(/\b([A-Z]{2})\s+(\d{5}(?:-\d{4})?)\b/);
            if (abbrevMatch) { state = abbrevMatch[1]; zip = abbrevMatch[2]; break; }

            // Try "Arizona 85281" format
            const fullMatch = part.match(/(\w+)\s+(\d{5}(?:-\d{4})?)/);
            if (fullMatch) {
              const stateAbbr = stateAbbrevs[fullMatch[1].toLowerCase()];
              if (stateAbbr) { state = stateAbbr; zip = fullMatch[2]; break; }
            }

            // Try just zip code
            const zipMatch = part.match(/\b(\d{5}(?:-\d{4})?)\b/);
            if (zipMatch && !zip) { zip = zipMatch[1]; }
          }

          const isValidAddress = streetAddress && streetAddress.length > 5;

          if (isValidAddress) {
            const result = {
              property_address: streetAddress,
              property_city: city || '',
              property_state: state,
              property_zip: zip,
              coordinates: suggestion.coordinates || null,
              confidence: suggestion.confidence || 0.8,
              validation_status: 'valid'
            };
            console.log('✅ Valid address parsed:', result);
            return result;
          }
        }

        // Fallback parsing for any format
        const result = {
          property_address: fullAddress,
          property_city: '',
          property_state: '',
          property_zip: '',
          coordinates: suggestion.coordinates || null,
          confidence: suggestion.confidence || 0.7,
          validation_status: 'valid'
        };
        console.log('📍 Fallback address parsed:', result);
        return result;
      } catch (error) {
        console.error('Error parsing address:', error);
        // Even on error, return a valid structure to allow MLS lookup
        return {
          property_address: fullAddress,
          property_city: '',
          property_state: 'AZ',
          property_zip: '',
          coordinates: null,
          confidence: 0.5,
          validation_status: 'valid' // Changed from 'error' to 'valid' to allow MLS lookup
        };
      }
    };

    const addressComponents = parseAddressComponents(suggestion.address);
    console.log('📍 Final parsed address components:', addressComponents);
    
    // Update form data with parsed components
    setFormData(prev => ({
      ...prev,
      ...addressComponents
    }));

    // NEW: Trigger enhanced seller CMA analysis with database persistence
    console.log('🚀 Starting enhanced seller CMA analysis for address:', suggestion.address);
    await performEnhancedSellerCMAAnalysis(suggestion.address, addressComponents);
  };

  // NEW: Enhanced Seller CMA Analysis with Database Persistence
  const performEnhancedSellerCMAAnalysis = async (address: string, addressComponents: any) => {
    if (!address || address.length < 10) return;

    // Lock the address immediately when starting analysis
    setAddressLocked(true);
    console.log('🔒 [SELLER CMA] Address locked for enhanced seller CMA analysis');

    setLookupStatus('loading');
    setLookupProgress(0);
    setLookupStep('Starting enhanced seller CMA analysis...');

    try {
      // Step-by-step progress simulation
      const progressSteps = [
        { progress: 15, step: 'Preparing seller property analysis...' },
        { progress: 30, step: 'Running seller-optimized CMA search...' },
        { progress: 50, step: 'Analyzing comparable properties...' },
        { progress: 70, step: 'Generating advanced AI market insights...' },
        { progress: 85, step: 'Saving to reports and lead context...' },
        { progress: 100, step: 'Enhanced seller analysis complete!' }
      ];

      // Simulate progress updates
      let currentProgressIndex = 0;
      const progressInterval = setInterval(() => {
        if (currentProgressIndex < progressSteps.length) {
          const step = progressSteps[currentProgressIndex];
          setLookupProgress(step.progress);
          setLookupStep(step.step);
          currentProgressIndex++;
        }
      }, 800);

      // Prepare seller profile data for the API
      const sellerProfileData = {
        bedrooms: formData.bedrooms || 0,
        bathrooms: formData.bathrooms || 0,
        square_footage: formData.square_footage || 0,
        year_built: formData.year_built || 0,
        lot_size_sqft: formData.lot_size_sqft || 0,
        garage_spaces: formData.garage_spaces || 0,
        pool: formData.pool || false,
        property_type: formData.property_type || 'single_family',
        property_condition: formData.property_condition || 'good',
        estimated_value: formData.estimated_value || 0,
        recent_improvements: formData.recent_improvements || false,
        improvements_cost: formData.improvements_cost || 0
      };

      // Call the enhanced seller CMA API endpoint
      const response = await axiosInstance.post('sellers/enhanced-cma/address-analysis', {
        address: address,
        lead_id: leadId,
        user_id: userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })(),
        seller_profile_data: sellerProfileData
      });

      clearInterval(progressInterval);
      setLookupProgress(100);

      if (response.data.success) {
        const analysisResults = response.data;
        console.log('✅ [SELLER CMA] Enhanced analysis complete:', analysisResults);

        // Extract key results
        const uiData = analysisResults.ui_data;
        const reportId = analysisResults.report_id;
        
        // Auto-populate seller profile form with enhanced data
        const updatedFormData = {
          ...formData,
          // Property address components (already set from address selection)
          property_address: addressComponents.property_address,
          property_city: addressComponents.property_city || '',
          property_state: addressComponents.property_state || 'AZ',
          property_zip: addressComponents.property_zip || '',
          
          // Enhanced valuation from CMA
          estimated_value: uiData.valuation.estimated_value || formData.estimated_value || 0,
          
          // Update seller score based on data completeness
          seller_score: Math.min(100, (formData.seller_score || 0) + 15),
          
          // Update AI insights with CMA data
          ai_insights: {
            ...formData.ai_insights,
            cma_analysis: uiData.market_analysis,
            valuation_data: uiData.valuation,
            report_id: reportId,
            last_updated: new Date().toISOString(),
            confidence_score: uiData.valuation.confidence_score,
            analysis_type: uiData.ai_insights.analysis_type
          }
        };

        console.log('📊 [SELLER CMA] Updated form data with CMA results:', updatedFormData);
        setFormData(updatedFormData);

        // Save the enhanced seller profile to database (using existing saveSellerProfile)
        setFormData(updatedFormData);
        await saveSellerProfile();

        setLookupStatus('success');
        setLookupStep('Enhanced seller analysis complete!');
        
        // Show success message with key insights
        const estimatedValue = uiData.valuation.final_estimated_value || uiData.valuation.estimated_value;
        const confidence = Math.round(uiData.valuation.confidence_score * 100);
        const comparables = uiData.market_analysis.total_comparables;
        const analysisType = uiData.ai_insights.analysis_type;
        
        // Enhanced success message with XGBoost info
        let successMessage = `🏠 Enhanced Seller Analysis Complete!\n`;
        successMessage += `💰 Estimated Value: $${estimatedValue?.toLocaleString()}\n`;
        successMessage += `📊 Confidence: ${confidence}%\n`;
        successMessage += `🏘️ Comparables: ${comparables} properties\n`;
        
        // Add XGBoost-specific information if available
        if (analysisType?.includes('xgboost')) {
          successMessage += `🤖 Analysis Type: AI + CMA Hybrid\n`;
          if (uiData.valuation.xgboost_estimate) {
            successMessage += `🧠 AI Valuation: $${uiData.valuation.xgboost_estimate?.toLocaleString()}\n`;
          }
          if (uiData.valuation.cma_estimate) {
            successMessage += `📈 CMA Estimate: $${uiData.valuation.cma_estimate?.toLocaleString()}\n`;
          }
        } else {
          successMessage += `📈 Analysis Type: Traditional CMA\n`;
        }
        
        successMessage += `📍 Market Position: ${uiData.valuation.market_position || 'Average'}`;
        
        toast.success(successMessage, {
          duration: 8000,
          icon: analysisType?.includes('xgboost') ? '🤖' : '🎯'
        });

        // Re-fetch seller profile and CMA reports so UI reflects updated DB data
        await loadSellerProfile();
        await loadCMAReports();

        // Keep address locked after successful analysis
        setTimeout(() => {
          setLookupProgress(0);
          setLookupStep('');
          console.log('🔒 [SELLER CMA] Keeping address locked after successful analysis');
        }, 2000);

      } else {
        throw new Error(response.data.error || 'Enhanced seller CMA analysis failed');
      }

    } catch (error: any) {
      console.error('🚨 [SELLER CMA] Enhanced analysis error:', error);
      setLookupStatus('error');
      setLookupStep('Seller analysis temporarily unavailable');
      
      // Unlock address on error
      setAddressLocked(false);
      console.log('🔓 [SELLER CMA] Address unlocked - error occurred');
      
      // Show specific error message
      const errorMessage = error.response?.data?.error || error.message || 'Analysis failed';
      toast.error(`Seller CMA Analysis Failed: ${errorMessage}`);
      
      setTimeout(() => {
        setLookupProgress(0);
        setLookupStep('');
      }, 3000);
    }
  };

  // Enhanced MLS Lookup - Same workflow as Step1PropertyInput.tsx (LEGACY - kept for fallback)
  const performMLSLookup = async (address: string) => {
    if (!address || address.length < 10) return;

    // Lock the address immediately when starting lookup
    setAddressLocked(true);
    console.log('🔒 [SELLER MLS] Address locked for enhanced property search');

    setLookupStatus('loading');
    setLookupProgress(0);
    setLookupStep('Starting comprehensive property analysis...');

    try {
      // Step-by-step progress simulation to show the enhanced workflow
      const progressSteps = [
        { progress: 10, step: 'Connecting to county property database...' },
        { progress: 25, step: 'Searching county records by address...' },
        { progress: 40, step: 'Retrieving property characteristics and APN...' },
        { progress: 55, step: 'Querying MLS database with multiple search methods...' },
        { progress: 70, step: 'Applying Smart CMA data prioritization...' },
        { progress: 85, step: 'Updating seller profile and lead context...' },
        { progress: 100, step: 'Enhanced property analysis complete!' }
      ];

      // Simulate progress updates
      let currentProgressIndex = 0;
      const progressInterval = setInterval(() => {
        if (currentProgressIndex < progressSteps.length) {
          const step = progressSteps[currentProgressIndex];
          setLookupProgress(step.progress);
          setLookupStep(step.step);
          currentProgressIndex++;
        }
      }, 800);

      // Make the enhanced API call with quick mode for faster UI response
      const baseUrl = `/api/properties/test-enhanced-search/${encodeURIComponent(address)}`;
      const params = new URLSearchParams();
      
      // Add customer name if available
      if (sellerProfile?.lead_first_name) {
        params.append('customerName', `${sellerProfile.lead_first_name} ${sellerProfile.lead_last_name}`);
      }
      
      // Enable quick mode for faster UI response (skips time-consuming comparable searches)
      params.append('quick', 'true');
      
      // OPTIMIZATION: Pass lead_id for direct lead_ai_context save
      params.append('lead_id', leadId);
      
      const apiUrl = `${baseUrl}?${params.toString()}`;
      console.log('🚀 [SELLER MLS] API URL with quick mode and lead_id:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      clearInterval(progressInterval);
      setLookupProgress(100);

      const data = await response.json();
      
      // Store report ID if available for workflow continuation
      if (data.report_id) {
        sessionStorage.setItem('currentReportId', data.report_id);
        console.log('🔗 [SELLER MLS] Stored report ID:', data.report_id);
      }

      if (data.result && data.result.success && data.result.mls_data) {
        const mlsData = data.result.mls_data;
        console.log('✅ [SELLER MLS] Enhanced property data retrieved:', mlsData);

        // Auto-populate seller profile form with prioritized data
        const updatedFormData = {
          ...formData,
          // Property address components (already set from address selection)
          property_address: formData.property_address,
          property_city: formData.property_city || mlsData.city || '',
          property_state: formData.property_state || 'AZ',
          property_zip: formData.property_zip || mlsData.postal_code || '',
          
          // Property characteristics from MLS
          bedrooms: mlsData.bedrooms || formData.bedrooms || 0,
          bathrooms: mlsData.bathrooms || formData.bathrooms || 0,
          square_footage: mlsData.living_area || formData.square_footage || 0,
          year_built: mlsData.year_built || formData.year_built || 0,
          lot_size_sqft: mlsData.lot_size || formData.lot_size_sqft || 0,
          garage_spaces: mlsData.garage_spaces !== undefined ? mlsData.garage_spaces : formData.garage_spaces || 0,
          pool: mlsData.pool !== undefined ? mlsData.pool : formData.pool || false,
          
          // Property valuation from MLS
          estimated_value: mlsData.list_price || mlsData.last_sale_price || formData.estimated_value || 0,
          
          // Property metadata
          property_type: mlsData.property_type || formData.property_type || 'single_family',
          occupancy_status: formData.occupancy_status || 'owner_occupied',
          property_condition: formData.property_condition || 'good',
          
          // Enhanced data from MLS lookup
          mls_data: mlsData,
          apn: mlsData.apn,
          mcr: mlsData.mcr,
          ownership: mlsData.ownership,
          subdivision: mlsData.subdivision,
          last_sale_date: mlsData.last_sale_date,
          last_sale_price: mlsData.last_sale_price
        };

        console.log('📊 [SELLER MLS] Updated form data:', updatedFormData);
        setFormData(updatedFormData);

        // Save the enhanced seller profile to database
        await saveEnhancedSellerProfile(updatedFormData, mlsData, data.report_id);

        setLookupStatus('success');
        setLookupStep('Enhanced property analysis complete!');
        
        // Show success message
        toast.success('Property data retrieved and seller profile updated!', {
          duration: 4000,
          icon: '🏠'
        });

        // Keep address locked after successful lookup
        setTimeout(() => {
          setLookupProgress(0);
          setLookupStep('');
          console.log('🔒 [SELLER MLS] Keeping address locked after successful lookup');
        }, 2000);

      } else {
        setLookupStatus('error');
        setLookupStep('Property not found in available databases');
        
        // Unlock address if property not found
        setAddressLocked(false);
        console.log('🔓 [SELLER MLS] Address unlocked - property not found');
        
        toast.error('Property not found in county or MLS databases');
        
        setTimeout(() => {
          setLookupProgress(0);
          setLookupStep('');
        }, 3000);
      }
    } catch (error: any) {
      console.error('🚨 [SELLER MLS] Enhanced lookup error:', error);
      setLookupStatus('error');
      setLookupStep('Search system temporarily unavailable');
      
      // Unlock address on error
      setAddressLocked(false);
      console.log('🔓 [SELLER MLS] Address unlocked - error occurred');
      
      toast.error('Property search temporarily unavailable');
      
      setTimeout(() => {
        setLookupProgress(0);
        setLookupStep('');
      }, 3000);
    }
  };

  // Enhanced seller profile save with MLS data and lead context update
  const saveEnhancedSellerProfile = async (profileData: any, mlsData: any, reportId?: string) => {
    try {
      console.log('💾 [SELLER SAVE] ===== STARTING ENHANCED SELLER PROFILE SAVE =====');
      console.log('💾 [SELLER SAVE] Function called with parameters:');
      console.log('💾 [SELLER SAVE] - profileData keys:', Object.keys(profileData));
      console.log('💾 [SELLER SAVE] - mlsData keys:', Object.keys(mlsData));
      console.log('💾 [SELLER SAVE] - reportId:', reportId);
      console.log('💾 [SELLER SAVE] - leadId:', leadId);
      console.log('💾 [SELLER SAVE] - userInfo?.id:', userInfo?.id);
      
      // Prepare enhanced seller profile data
      const enhancedProfileData = {
        ...profileData,
        // Add MLS metadata
        ai_insights: {
          ...profileData.ai_insights,
          mls_lookup_date: new Date().toISOString(),
          mls_confidence: mlsData.confidence_score || 0.85,
          data_sources: {
            county_found: !!(mlsData.apn || mlsData.mcr),
            mls_found: !!(mlsData.mls_listings && mlsData.mls_listings.length > 0),
            search_methods: mlsData.search_methods_used || []
          },
          report_id: reportId
        },
        // Update seller score based on data completeness
        seller_score: calculateSellerScore(profileData, mlsData),
        updated_on: new Date().toISOString()
      };

      // Map enum values to database-compliant format
      const mappedEnhancedData = mapFormDataToDatabase(enhancedProfileData);

      console.log('💾 [SELLER SAVE] Enhanced profile data prepared:');
      console.log('💾 [SELLER SAVE] - Final data keys:', Object.keys(mappedEnhancedData));
      console.log('💾 [SELLER SAVE] - Sample values:');
      console.log('   - Bedrooms:', mappedEnhancedData.bedrooms);
      console.log('   - Bathrooms:', mappedEnhancedData.bathrooms);
      console.log('   - Square Footage:', mappedEnhancedData.square_footage);
      console.log('   - Year Built:', mappedEnhancedData.year_built);
      console.log('   - Estimated Value:', mappedEnhancedData.estimated_value);
      console.log('   - Seller Score:', mappedEnhancedData.seller_score);

      const apiUrl = `/api/sellers/${leadId}/profile`;
      console.log('💾 [SELLER SAVE] API URL constructed:', apiUrl);
      console.log('💾 [SELLER SAVE] Full request URL will be:', window.location.origin + apiUrl);

      // Save to seller profile
      console.log('💾 [SELLER SAVE] Making PUT request to seller profile API...');
      console.log('💾 [SELLER SAVE] Request details:');
      console.log('💾 [SELLER SAVE] - Method: PUT');
      console.log('💾 [SELLER SAVE] - URL:', apiUrl);
      console.log('💾 [SELLER SAVE] - Headers:', {
        'X-User-Id': userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })(),
        'Content-Type': 'application/json'
      });
      console.log('💾 [SELLER SAVE] - Data size:', JSON.stringify(mappedEnhancedData).length, 'characters');
      console.log('💾 [SELLER SAVE] - Mapped enhanced data:', mappedEnhancedData);

      // Use Node.js API server (port 3001) instead of Flask API due to connection pool issues
      console.log('💾 [SELLER SAVE] Using Node.js API server instead of Flask');
      
      const response = await axiosInstance.put(apiUrl, mappedEnhancedData, {
        headers: {
          'X-User-Id': userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })()
        }
      });
      
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const responseData = response.data;

      console.log('💾 [SELLER SAVE] ✅ PUT request completed successfully!');
      console.log('💾 [SELLER SAVE] Response details:');
      console.log('💾 [SELLER SAVE] - Status:', response.status);
      console.log('💾 [SELLER SAVE] - Status Text:', response.statusText);
      console.log('💾 [SELLER SAVE] - Headers:', response.headers);
      console.log('💾 [SELLER SAVE] - Response data:', responseData);

      if (responseData.status === 'success') {
        console.log('✅ [SELLER SAVE] Seller profile updated successfully');
        
        // Update lead AI context with enhanced property data
        console.log('🧠 [SELLER SAVE] Updating lead AI context...');
        await updateLeadAIContext(profileData, mlsData, reportId);
        
        // Refresh the seller profile to show updated data
        console.log('🔄 [SELLER SAVE] Refreshing seller profile...');
        await loadSellerProfile();
        
        console.log('🎉 [SELLER SAVE] All operations completed successfully');
        return true;
      } else {
        console.error('❌ [SELLER SAVE] Server returned error status:', responseData.status);
        console.error('❌ [SELLER SAVE] Server error details:', responseData);
        toast.error(`Failed to save: ${responseData.error || 'Unknown server error'}`);
        return false;
      }
    } catch (error: any) {
      console.error('🚨 [SELLER SAVE] ===== EXCEPTION CAUGHT DURING SAVE =====');
      console.error('🚨 [SELLER SAVE] Error type:', error.constructor.name);
      console.error('🚨 [SELLER SAVE] Error message:', error.message);
      console.error('🚨 [SELLER SAVE] Error stack:', error.stack);
      
      if (error.response) {
        console.error('🚨 [SELLER SAVE] HTTP Response Error Details:');
        console.error('   - Status:', error.response.status);
        console.error('   - Status Text:', error.response.statusText);
        console.error('   - Response Data:', error.response.data);
        console.error('   - Response Headers:', error.response.headers);
        console.error('   - Request URL:', error.response.config?.url);
        console.error('   - Request Method:', error.response.config?.method);
        console.error('   - Request Headers:', error.response.config?.headers);
        
        toast.error(`HTTP ${error.response.status}: ${error.response.data?.error || error.response.statusText}`);
      } else if (error.request) {
        console.error('🚨 [SELLER SAVE] Network Error - No response received:');
        console.error('   - Request details:', error.request);
        console.error('   - Request URL:', error.config?.url);
        console.error('   - Request method:', error.config?.method);
        toast.error('Network error - please check your connection');
      } else {
        console.error('🚨 [SELLER SAVE] Request Setup Error:', error.message);
        toast.error(`Request error: ${error.message}`);
      }
      
      return false;
    }
  };

  // Update lead AI context with enhanced property data
  const updateLeadAIContext = async (profileData: any, mlsData: any, reportId?: string) => {
    try {
      console.log('🧠 [AI CONTEXT] Updating lead AI context...');
      
      const contextUpdate = {
        lead_id: leadId,
        property_context: {
          address: profileData.property_address,
          city: profileData.property_city,
          state: profileData.property_state,
          zip: profileData.property_zip,
          property_type: profileData.property_type,
          bedrooms: profileData.bedrooms,
          bathrooms: profileData.bathrooms,
          square_footage: profileData.square_footage,
          year_built: profileData.year_built,
          lot_size: profileData.lot_size_sqft,
          estimated_value: profileData.estimated_value,
          mls_data: mlsData,
          apn: mlsData.apn,
          ownership: mlsData.ownership
        },
        seller_context: {
          selling_timeframe: profileData.selling_timeframe,
          reason_for_selling: profileData.reason_for_selling,
          motivation_level: profileData.motivation_level,
          seller_stage: profileData.seller_stage,
          is_also_buyer: profileData.is_also_buyer
        },
        market_context: {
          report_id: reportId,
          last_updated: new Date().toISOString(),
          data_sources: mlsData.data_sources || {}
        }
      };

      // Call lead AI context update endpoint on Node.js API
      const response = await axiosInstance.post('/lead-ai-context/update', contextUpdate, {
        headers: {
          'X-User-Id': userInfo?.id || (() => { try { const t = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || ''; return t ? JSON.parse(atob(t.split('.')[1])).id : ''; } catch(e) { return ''; } })()
        }
      });

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const responseData = response.data;

      if (responseData.status === 'success') {
        console.log('✅ [AI CONTEXT] Lead AI context updated successfully');
        return true;
      } else {
        console.warn('⚠️ [AI CONTEXT] Failed to update lead AI context:', responseData);
        return false;
      }
    } catch (error: any) {
      console.error('🚨 [AI CONTEXT] Error updating lead AI context:', error);
      // Don't show error to user as this is background process
      return false;
    }
  };

  // Calculate seller score based on data completeness and MLS data
  const calculateSellerScore = (profileData: any, mlsData: any): number => {
    let score = 0;
    
    // Base profile completeness (40 points)
    if (profileData.selling_timeframe) score += 5;
    if (profileData.reason_for_selling) score += 5;
    if (profileData.motivation_level) score += 5;
    if (profileData.property_address) score += 10;
    if (profileData.bedrooms > 0) score += 5;
    if (profileData.bathrooms > 0) score += 5;
    if (profileData.square_footage > 0) score += 5;
    
    // MLS data quality bonus (30 points)
    if (mlsData.apn) score += 10;
    if (mlsData.mls_listings && mlsData.mls_listings.length > 0) score += 10;
    if (mlsData.confidence_score && mlsData.confidence_score > 0.8) score += 10;
    
    // Property valuation data (30 points)
    if (profileData.estimated_value > 0) score += 15;
    if (mlsData.last_sale_price) score += 10;
    if (mlsData.list_price) score += 5;
    
    return Math.min(score, 100); // Cap at 100
  };

  const handleSaveEditing = async () => {
    try {
      // Update the property address in formData if it was changed
      if (addressValue && addressValue !== sellerProfile?.property_address) {
        setFormData(prev => ({
          ...prev,
          property_address: addressValue
        }));
      }
      
      // Save the profile (saveSellerProfile handles its own loading states and toasts)
      await saveSellerProfile();
      
    } catch (error) {
      console.error('Error in handleSaveEditing:', error);
      // Don't show duplicate error toasts since saveSellerProfile handles this
    }
  };

  const handleToggleEdit = async () => {
    console.log('🔄 handleToggleEdit called, current editing state:', editing);
    if (editing) {
      console.log('💾 In editing mode, calling handleSaveEditing...');
      await handleSaveEditing();
      console.log('✅ Save completed - saveSellerProfile handles setEditing(false)');
    } else {
      console.log('✏️ Setting editing to true');
      setEditing(true);
      setAddressValue(sellerProfile?.property_address || '');
      console.log('📝 Editing state should now be true, addressValue set to:', sellerProfile?.property_address || '');
    }
  };

  const getSellerStageColor = (stage: string) => {
    switch (stage) {
      case 'considering': return 'secondary';
      case 'researching': return 'info';
      case 'preparing': return 'warning';
      case 'ready_to_list': return 'primary';
      case 'listing': return 'success';
      case 'under_contract': return 'success';
      case 'closed': return 'dark';
      default: return 'light';
    }
  };

  const getMotivationColor = (level: string) => {
    switch (level) {
      case 'urgent': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'secondary';
      default: return 'light';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getProgressBarVariant = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 80) return 'primary';
    if (score >= 70) return 'warning';
    return 'danger';
  };

  const getScoreCardClass = (score: number) => {
    if (score >= 90) return 'bg-success';
    if (score >= 80) return 'bg-primary';
    if (score >= 70) return 'bg-warning';
    return 'bg-danger';
  };

  // Enhanced Smart CMA integration with XGBoost
  const launchSmartCMA = async () => {
    if (!sellerProfile) {
      toast.error('Please save seller profile before generating CMA');
      return;
    }

    setLoading(true);
    
    try {
      // Prepare enhanced property data for CMA + XGBoost analysis
      const enhancedPropertyData = {
        subject_property: {
          // Core property characteristics
          address: `${sellerProfile.property_address}, ${sellerProfile.property_city}, ${sellerProfile.property_state} ${sellerProfile.property_zip}`,
          bedrooms: sellerProfile.bedrooms,
          bathrooms: sellerProfile.bathrooms,
          living_area: sellerProfile.square_footage,
          year_built: sellerProfile.year_built,
          lot_size: sellerProfile.lot_size_sqft,
          garage_spaces: sellerProfile.garage_spaces,
          pool: sellerProfile.pool,
          property_type: sellerProfile.property_type,
          
          // Location data for accurate searches
          city: sellerProfile.property_city,
          state: sellerProfile.property_state,
          postal_code: sellerProfile.property_zip,
          
          // Enhanced features for XGBoost model
          property_condition: sellerProfile.property_condition,
          recent_improvements: sellerProfile.recent_improvements,
          improvements_cost: sellerProfile.improvements_cost || 0,
          occupancy_status: sellerProfile.occupancy_status,
          
          // Seller pricing context
          estimated_value: sellerProfile.estimated_value,
          desired_price: sellerProfile.desired_price,
          minimum_price: sellerProfile.minimum_price,
          
          // Coordinates if available from MLS lookup (try multiple field names)
          lat: sellerProfile.mls_data?.latitude || 
               sellerProfile.mls_data?.Latitude ||
               (sellerProfile.mls_data?.mls_listings?.[0]?.StandardFields?.Latitude) ||
               (sellerProfile.mls_data?.location?.latitude),
          lng: sellerProfile.mls_data?.longitude || 
               sellerProfile.mls_data?.Longitude ||
               (sellerProfile.mls_data?.mls_listings?.[0]?.StandardFields?.Longitude) ||
               (sellerProfile.mls_data?.location?.longitude)
        },
        
        // MLS listing ID for enhanced accuracy (if available)
        mls_listing_id: sellerProfile.mls_data?.listing_id,
        
        // Analysis configuration
        options: {
          months_back: 12,
          max_distance_miles: 1.0,
          max_results: 15,
          include_xgboost_analysis: true,
          analysis_type: 'seller_comprehensive',
          use_enhanced_cma: true
        },
        
        // Context for report generation
        lead_id: leadId,
        user_id: userInfo?.id,
        seller_context: {
          seller_name: `${sellerProfile.lead_first_name} ${sellerProfile.lead_last_name}`,
          seller_email: sellerProfile.lead_email,
          seller_phone: sellerProfile.lead_phone,
          selling_timeframe: sellerProfile.selling_timeframe,
          motivation_level: sellerProfile.motivation_level,
          pricing_strategy: sellerProfile.pricing_strategy
        }
      };

      // Debug coordinate extraction
      console.log('🗺️ [CMA DEBUG] MLS Data structure:', sellerProfile.mls_data);
      console.log('🗺️ [CMA DEBUG] Extracted coordinates:', {
        lat: enhancedPropertyData.subject_property.lat,
        lng: enhancedPropertyData.subject_property.lng
      });

      // Show loading toast
      toast('🤖 Running Enhanced CMA + AI Analysis...', { duration: Infinity, id: 'cma-loading' });

      // Call enhanced CMA API endpoint
      const response = await axiosInstance.post('/enhanced-cma/seller-analysis', enhancedPropertyData);
      
      // Dismiss loading toast
      toast.dismiss('cma-loading');
      
      if (response.data.status === 'success') {
        const analysisResults = response.data.analysis_results;
        const insights = analysisResults.enhanced_market_insights || analysisResults.market_insights;
        const estimatedValue = insights?.final_estimated_value || insights?.estimated_value;
        const confidence = insights?.confidence_score || 0.85;
        const analysisType = analysisResults.analysis_type;
        
        // Show success message with enhanced insights
        let successMessage = `🎉 Enhanced CMA Analysis Complete!\n`;
        successMessage += `💰 Estimated Value: $${estimatedValue?.toLocaleString()}\n`;
        successMessage += `📊 Confidence: ${(confidence * 100).toFixed(1)}%\n`;
        successMessage += `🏠 Comparables Found: ${analysisResults.total_found}`;
        
        // Add XGBoost-specific insights if available
        if (analysisType?.includes('xgboost')) {
          const xgboostPred = analysisResults.xgboost_prediction;
          const modelPerf = analysisResults.model_performance;
          
          if (xgboostPred) {
            successMessage += `\n🤖 AI Valuation: $${xgboostPred.predicted_price?.toLocaleString()}`;
          }
          
          if (modelPerf?.average_accuracy) {
            successMessage += `\n🎯 Model Accuracy: ${(modelPerf.average_accuracy * 100).toFixed(1)}%`;
          }
        }
        
        toast.success(successMessage, { duration: 8000 });
        
        // Refresh CMA reports to show the new analysis
        await loadCMAReports();
        
        // Navigate to report details if report was saved
        if (response.data.analysis_results.report_id) {
          navigate(`/smart-cma-report/${response.data.analysis_results.report_id}`);
        }
        
      } else {
        throw new Error(response.data.error || 'Enhanced CMA analysis failed');
      }
      
    } catch (error: any) {
      console.error('Enhanced CMA analysis failed:', error);
      toast.dismiss('cma-loading');
      
      // Provide helpful error messages
      let errorMessage = 'Enhanced CMA Analysis Failed';
      if (error.response?.status === 404) {
        errorMessage = 'CMA service not available - please try again later';
      } else if (error.response?.data?.error) {
        errorMessage = `Analysis Error: ${error.response.data.error}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const viewCMAReport = (reportId: string) => {
    // Build shareable CMA report URL using current domain + agent ID
    const currentHost = window.location.hostname;
    // Get agent ID from JWT token or userInfo
    let agentId = userInfo?.id || '';
    if (!agentId) {
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || '';
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          agentId = payload.id || '';
        }
      } catch (e) { /* ignore parse errors */ }
    }

    // Use aisearchportal for dev/prod, fall back to current domain
    let baseUrl;
    if (currentHost.includes('dev.') || currentHost === 'localhost') {
      baseUrl = currentHost === 'localhost'
        ? `http://localhost:${window.location.port || '3000'}`
        : 'https://dev.aisearchportal.com';
    } else {
      baseUrl = 'https://aisearchportal.com';
    }

    const chatUrl = `${baseUrl}/cma-report/${reportId}${agentId ? `?agentId=${agentId}` : ''}`;
    window.open(chatUrl, '_blank');
    console.log('Opening CMA report in new tab:', chatUrl);
  };

  // AI Chat Functions
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const startProfileBuilding = (workflowType: string) => {
    setShowProfileBuilder(false);
    setShowEmbeddedChat(true);
    
    let welcomeMessage = "";
    
    switch (workflowType) {
      case 'property-details':
        welcomeMessage = `Hi ${sellerProfile?.lead_first_name || 'there'}! 👋 I'm here to help you gather comprehensive details about your property for listing preparation.\n\nI'll guide you through:\n• Property condition and recent improvements\n• Unique features and amenities\n• Neighborhood highlights\n• Any concerns or special considerations\n\nThis usually takes 5-10 minutes. Ready to get started with your property details?`;
        break;
      case 'pricing-strategy':
        welcomeMessage = `Hello ${sellerProfile?.lead_first_name || 'there'}! 💰 Let's work together to develop the optimal pricing strategy for your property.\n\nWe'll discuss:\n• Your desired price range and flexibility\n• Market timing preferences\n• Negotiation comfort level\n• Financial goals and timeline\n\nThis conversation typically takes 8-12 minutes. Shall we begin exploring your pricing strategy?`;
        break;
      case 'timeline-motivation':
        welcomeMessage = `Hi ${sellerProfile?.lead_first_name || 'there'}! 📅 I'm here to understand your selling timeline and what's motivating your move.\n\nWe'll cover:\n• Your ideal selling timeframe\n• Reason for selling and urgency level\n• Moving plans and coordination needs\n• Any time-sensitive factors\n\nThis usually takes 6-10 minutes. Ready to discuss your timeline and motivation?`;
        break;
      case 'complete-profile':
        welcomeMessage = `Welcome ${sellerProfile?.lead_first_name || 'there'}! 🎯 I'm excited to help you build a complete seller profile that will maximize your success.\n\nIn our comprehensive session, we'll cover:\n• Complete property details and condition\n• Pricing strategy and market positioning\n• Timeline, motivation, and flexibility\n• Marketing preferences and showing logistics\n• Any special circumstances or requirements\n\nThis is our most thorough conversation, typically taking 15-20 minutes. Ready to build your complete seller profile?`;
        break;
      default:
        welcomeMessage = `Hi ${sellerProfile?.lead_first_name || 'there'}! I'm here to help you with your selling journey. How can I assist you today?`;
    }

    const initialMessage: Message = {
      id: Date.now(),
      text: welcomeMessage,
      sender: "assistant",
      timestamp: new Date(),
      type: "text"
    };

    setMessages([initialMessage]);
    setActiveTab('ai-chat');
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage: Message = {
      id: Date.now(),
      text: inputMessage,
      sender: "user",
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsTyping(true);

    try {
      const response = await axiosInstance.post('/chat/seller-profile', {
        message: inputMessage,
        leadId: leadId,
        sellerProfile: sellerProfile,
        conversationId: currentConversationId,
        context: {
          sellerName: `${sellerProfile?.lead_first_name} ${sellerProfile?.lead_last_name}`,
          propertyAddress: sellerProfile?.property_address,
          sellingTimeframe: sellerProfile?.selling_timeframe,
          motivationLevel: sellerProfile?.motivation_level,
          propertyType: sellerProfile?.property_type,
          estimatedValue: sellerProfile?.estimated_value
        }
      });

      const assistantMessage: Message = {
        id: Date.now() + 1,
        text: response.data.message,
        sender: "assistant",
        timestamp: new Date(),
        type: response.data.type || "text"
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      if (response.data.conversationId) {
        setCurrentConversationId(response.data.conversationId);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: Date.now() + 1,
        text: "I apologize, but I'm having trouble connecting right now. Please try again in a moment.",
        sender: "assistant",
        timestamp: new Date(),
        type: "text"
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to send message');
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderProfileBuilderIntro = () => {
    return (
      <div className="profile-builder-intro">
        <div className="text-center mb-4">
          <div className="neural-icon-wrapper mx-auto mb-3">
            <FontAwesomeIcon icon={faRobot} className="neural-icon" />
          </div>
          <h3 className="mb-2">Seller Profile Builder</h3>
          <p className="text-muted">
            Let our AI guide you through building a comprehensive seller profile to maximize your success
          </p>
        </div>

        <Alert variant="info" className="mb-4">
          <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
          <strong>How It Works:</strong>
          <ol className="mb-0 mt-2">
            <li>Choose a specific area to focus on or complete profile building</li>
            <li>Have a natural conversation with our AI assistant</li>
            <li>Your responses automatically update your seller profile</li>
            <li>Get personalized insights and recommendations</li>
          </ol>
        </Alert>

        <Row className="g-3">
          <Col md={6}>
            <Card 
              className="h-100 border-0 shadow-sm workflow-card"
              style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
              onClick={() => startProfileBuilding('property-details')}
            >
              <Card.Body className="text-center p-4">
                <div className="mb-3">
                  <FontAwesomeIcon 
                    icon={faHome} 
                    size="2x" 
                    style={{ color: '#28a745' }}
                  />
                </div>
                <h5 className="card-title">🏠 Property Details Discovery</h5>
                <p className="card-text text-muted small">
                  Comprehensive property condition, features, improvements, and unique selling points
                </p>
                <Badge bg="success" className="mt-2">5-10 minutes</Badge>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card 
              className="h-100 border-0 shadow-sm workflow-card"
              style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
              onClick={() => startProfileBuilding('pricing-strategy')}
            >
              <Card.Body className="text-center p-4">
                <div className="mb-3">
                  <FontAwesomeIcon 
                    icon={faDollarSign} 
                    size="2x" 
                    style={{ color: '#ffc107' }}
                  />
                </div>
                <h5 className="card-title">💰 Pricing Strategy Discussion</h5>
                <p className="card-text text-muted small">
                  Desired price, flexibility, market positioning, and negotiation preferences
                </p>
                <Badge bg="warning" className="mt-2">8-12 minutes</Badge>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card 
              className="h-100 border-0 shadow-sm workflow-card"
              style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
              onClick={() => startProfileBuilding('timeline-motivation')}
            >
              <Card.Body className="text-center p-4">
                <div className="mb-3">
                  <FontAwesomeIcon 
                    icon={faCalendar} 
                    size="2x" 
                    style={{ color: '#17a2b8' }}
                  />
                </div>
                <h5 className="card-title">📅 Timeline & Motivation</h5>
                <p className="card-text text-muted small">
                  Selling timeline, reasons for moving, urgency level, and coordination needs
                </p>
                <Badge bg="info" className="mt-2">6-10 minutes</Badge>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card 
              className="h-100 border-0 shadow-sm workflow-card"
              style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
              onClick={() => startProfileBuilding('complete-profile')}
            >
              <Card.Body className="text-center p-4">
                <div className="mb-3">
                  <FontAwesomeIcon 
                    icon={faStar} 
                    size="2x" 
                    style={{ color: '#6f42c1' }}
                  />
                </div>
                <h5 className="card-title">🎯 Complete Profile Building</h5>
                <p className="card-text text-muted small">
                  Comprehensive session covering all aspects of your selling journey
                </p>
                <Badge bg="primary" className="mt-2">15-20 minutes</Badge>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <style>{`
          .workflow-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
          }
        `}</style>
      </div>
    );
  };

  const renderChatInterface = () => {
    return (
      <div className="chat-interface">
        <div className="chat-messages" style={{ height: '400px', overflowY: 'auto', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.sender === 'user' ? 'user-message' : 'assistant-message'} mb-3`}
            >
              <div className={`d-flex ${message.sender === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                <div
                  className={`message-bubble p-3 rounded-3 ${
                    message.sender === 'user' 
                      ? 'bg-primary text-white' 
                      : 'bg-white border'
                  }`}
                  style={{ 
                    maxWidth: '70%',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {message.text}
                </div>
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="message assistant-message mb-3">
              <div className="d-flex justify-content-start">
                <div className="message-bubble p-3 rounded-3 bg-white border" style={{ maxWidth: '70%' }}>
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input mt-3">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Type your message..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isTyping}
            />
            <Button 
              variant="primary" 
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isTyping}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </Button>
          </InputGroup>
        </div>

        <style>{`
          .typing-indicator {
            display: flex;
            align-items: center;
            gap: 4px;
          }
          
          .typing-indicator span {
            height: 8px;
            width: 8px;
            background-color: #6c757d;
            border-radius: 50%;
            display: inline-block;
            animation: typing 1.4s infinite ease-in-out;
          }
          
          .typing-indicator span:nth-child(1) {
            animation-delay: -0.32s;
          }
          
          .typing-indicator span:nth-child(2) {
            animation-delay: -0.16s;
          }
          
          @keyframes typing {
            0%, 80%, 100% {
              transform: scale(0.8);
              opacity: 0.5;
            }
            40% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>Loading seller profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="seller-profile-page">
      <style>{`
        .seller-profile-page {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          min-height: 100vh;
          padding: 20px;
        }

        .main-card {
          background: rgba(255, 255, 255, 0.95);
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(102, 126, 234, 0.1);
          border: 1px solid rgba(102, 126, 234, 0.2);
          backdrop-filter: blur(10px);
          overflow: hidden;
          margin: 0 auto;
          max-width: 1400px;
        }

        .compact-header {
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%);
          padding: 30px 40px;
          border-bottom: 1px solid rgba(102, 126, 234, 0.1);
          position: relative;
          overflow: hidden;
        }

        .compact-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMiIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIi8+Cjwvc3ZnPg==') repeat;
          opacity: 0.3;
        }

        .neural-icon-wrapper {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 30px rgba(139, 92, 246, 0.3);
          position: relative;
        }

        .neural-icon {
          font-size: 28px;
          color: white;
          z-index: 1;
        }

        .ai-status-badge {
          padding: 12px 20px;
          border-radius: 25px;
          font-weight: 700;
          letter-spacing: 0.8px;
          box-shadow: 0 8px 25px rgba(40, 167, 69, 0.4);
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: 2px solid rgba(255, 255, 255, 0.2);
          text-transform: uppercase;
          font-size: 0.85rem;
        }

        .metric-card {
          border: none;
          border-radius: 20px;
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(10px);
          cursor: pointer;
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .metric-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.1) 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .metric-card:hover {
          transform: translateY(-10px) scale(1.03);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
        }

        .metric-card:hover::before {
          opacity: 1;
        }

        .metric-icon {
          font-size: 2.2rem;
          opacity: 0.9;
          margin-bottom: 8px;
        }

        .metric-value {
          font-size: 2.8rem;
          font-weight: 800;
          margin: 8px 0 4px 0;
          background: linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .metric-label {
          font-size: 0.9rem;
          opacity: 0.8;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #6b7280;
        }

        .nav-tabs {
          border-bottom: none;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          padding: 0 40px;
          margin: 0;
          position: relative;
        }

        .nav-tabs::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.1) 50%, transparent 100%);
        }

        .nav-tabs .nav-link {
          border: none;
          border-radius: 12px 12px 0 0;
          font-weight: 700;
          color: #64748b;
          background: transparent;
          margin-right: 8px;
          margin-bottom: -1px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          text-transform: uppercase;
          font-size: 0.8rem;
          letter-spacing: 1px;
          padding: 16px 24px;
          position: relative;
        }

        .nav-tabs .nav-link::before {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 3px;
          background: linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%);
          border-radius: 2px;
          transition: width 0.3s ease;
        }

        .nav-tabs .nav-link.active {
          background: white;
          color: #8b5cf6;
          box-shadow: 0 -5px 15px rgba(0, 0, 0, 0.1);
        }

        .nav-tabs .nav-link.active::before {
          width: 30px;
        }

        .nav-tabs .nav-link:hover:not(.active) {
          background: rgba(255, 255, 255, 0.7);
          color: #475569;
          transform: translateY(-2px);
        }

        .tab-content {
          background: white;
          padding: 40px;
          border-radius: 0 0 20px 20px;
          min-height: 600px;
        }

        .consumer-ai-banner {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%);
          border: none;
          border-radius: 0;
          color: #8b5cf6;
          padding: 16px 40px;
          margin: 0;
          position: relative;
          overflow: hidden;
        }

        .consumer-ai-banner::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, transparent 0%, rgba(255, 255, 255, 0.1) 100%);
        }

        .compact-title {
          font-size: 1.8rem;
          font-weight: 700;
          margin: 0;
          position: relative;
          z-index: 1;
        }

        .compact-subtitle {
          font-size: 1rem;
          opacity: 0.9;
          margin: 0;
          position: relative;
          z-index: 1;
        }

        .trend-indicator {
          position: absolute;
          top: 15px;
          right: 15px;
          padding: 6px 12px;
          border-radius: 15px;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
        }

        .trend-up {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
        }

        .trend-down {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
        }

        .trend-neutral {
          background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(107, 114, 128, 0.3);
        }

        .text-purple {
          color: #8b5cf6 !important;
        }

        .bg-purple {
          background: linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%) !important;
        }

        .border-purple {
          border-color: #8b5cf6 !important;
        }

        .card {
          border-radius: 16px;
          border: 1px solid rgba(0, 0, 0, 0.05);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
          transform: translateY(-2px);
        }

        .card-header {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
          font-weight: 600;
          padding: 20px 24px;
        }

        .workflow-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(139, 92, 246, 0.1);
        }

        .workflow-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 40px rgba(139, 92, 246, 0.15);
          border-color: rgba(139, 92, 246, 0.3);
        }

        .typing-indicator {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .typing-indicator span {
          height: 8px;
          width: 8px;
          background: linear-gradient(135deg, #00d4ff 0%, #00bcd4 100%);
          border-radius: 50%;
          display: inline-block;
          animation: typing 1.4s infinite ease-in-out;
        }
        
        .typing-indicator span:nth-child(1) {
          animation-delay: -0.32s;
        }
        
        .typing-indicator span:nth-child(2) {
          animation-delay: -0.16s;
        }
        
        @keyframes typing {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .btn {
          border-radius: 12px;
          font-weight: 600;
          letter-spacing: 0.5px;
          transition: all 0.3s ease;
          text-transform: uppercase;
          font-size: 0.85rem;
        }

        .btn-primary {
          background: linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%);
          border: none;
          box-shadow: 0 4px 15px rgba(139, 92, 246, 0.3);
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(139, 92, 246, 0.4);
        }

        .btn-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
        }

        .btn-success:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
        }

        .badge {
          font-weight: 600;
          letter-spacing: 0.5px;
          padding: 6px 12px;
          border-radius: 8px;
        }
      `}</style>

      <div className="main-card">
        {/* Enhanced AI Header */}
        <div 
          className="compact-header"
          style={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            borderRadius: '16px 16px 0 0',
            padding: '32px 40px',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            backdropFilter: 'blur(10px)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <div 
                className="neural-icon-wrapper me-4"
                style={{
                  width: '64px',
                  height: '64px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 20px rgba(139, 92, 246, 0.4)',
                  border: '2px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <FontAwesomeIcon 
                  icon={faRobot} 
                  className="text-white" 
                  style={{ 
                    fontSize: '28px',
                    filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' 
                  }} 
                />
              </div>
            <div>
                <h1 
                  className="compact-title mb-2 fw-bold"
                  style={{ 
                    color: '#1f2937',
                    textShadow: '0 0 10px rgba(139, 92, 246, 0.3)',
                    fontSize: '1.8rem'
                  }}
                >
                  Seller AI Intelligence
              </h1>
              <p 
                className="compact-subtitle mb-0"
                style={{ 
                  color: '#6b7280',
                  fontSize: '14px',
                  opacity: 0.9
                }}
              >
                  AI-powered market insights for {sellerProfile?.lead_first_name} {sellerProfile?.lead_last_name}
              </p>
            </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div className="text-end me-3">
                <div className="text-muted small">AI Confidence</div>
                <div className="text-dark fw-bold">{sellerMetrics.aiConfidence}%</div>
              </div>
              <div 
                className="d-flex align-items-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                  padding: '12px 20px',
                  borderRadius: '25px',
                  border: '1px solid rgba(16, 185, 129, 0.3)'
                }}
              >
                <div 
                  className="rounded-circle me-2"
                  style={{
                    width: '8px',
                    height: '8px',
                    background: '#10b981',
                    animation: 'pulse 2s infinite'
                  }}
                ></div>
                <span 
                  className="fw-semibold text-success"
                  style={{ 
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  AI READY
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* AI Intelligence Banner */}
        <div 
          className="consumer-ai-banner border-0 mb-0"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)',
            borderRadius: '0',
            padding: '20px 40px',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            backdropFilter: 'blur(10px)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div 
                className="me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
                  boxShadow: '0 0 15px rgba(139, 92, 246, 0.4)'
                }}
              >
                <FontAwesomeIcon 
                  icon={faBrain} 
                  className="text-white" 
                  style={{ 
                    fontSize: '20px',
                    filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' 
                  }} 
                />
              </div>
              <div>
                <strong 
                  className="text-dark"
                  style={{
                    textShadow: '0 0 10px rgba(139, 92, 246, 0.3)',
                    fontSize: '16px'
                  }}
                >
                  Neural Market Intelligence Active
                </strong>
                <div className="text-muted small mt-1">
                  Real-time market analysis • Advanced AI predictions • 57-point property evaluation
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div className="text-center">
                <div className="text-muted small">CMA Reports</div>
                <div 
                  className="text-dark fw-bold"
                  style={{ textShadow: '0 0 5px rgba(139, 92, 246, 0.2)' }}
                >
                  {cmaReports.length}
                </div>
              </div>
              <div className="text-center">
                <div className="text-muted small">Data Sources</div>
                <div 
                  className="text-dark fw-bold"
                  style={{ textShadow: '0 0 5px rgba(139, 92, 246, 0.2)' }}
                >
                  5+
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Intelligence Metrics */}
        <div className="p-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
          <div className="mb-3">
            <h6 className="text-muted mb-0 text-uppercase fw-bold ls-1" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>
              <FontAwesomeIcon icon={faBrain} className="me-2 text-cyan" />
              Neural Intelligence Dashboard
            </h6>
          </div>
          <Row className="g-2">
            {/* Seller Score Card */}
            <Col className="d-flex">
              <div 
                className="metric-card text-center flex-fill"
                style={{
                  background: 'rgba(245, 158, 11, 0.1)',
                  borderRadius: '12px',
                  padding: '20px 12px',
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  minHeight: '110px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <div 
                  className="position-absolute top-0 end-0 px-2 py-1 rounded-bottom-start text-white"
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    fontSize: '10px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  HIGH
                </div>
                <FontAwesomeIcon icon={faStar} className="text-warning mb-2" size="lg" />
                <div className="fw-bold text-warning" style={{ fontSize: '20px' }}>
                  {sellerMetrics.sellerScore}
                </div>
                <small className="text-muted">Seller Readiness</small>
              </div>
            </Col>

            {/* Market Position Card */}
            <Col className="d-flex">
              <div 
                className="metric-card text-center flex-fill"
                style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: '12px',
                  padding: '20px 12px',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  minHeight: '110px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <div 
                  className="position-absolute top-0 end-0 px-2 py-1 rounded-bottom-start text-white"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    fontSize: '10px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  STRONG
                </div>
                <FontAwesomeIcon icon={faChartLine} className="text-success mb-2" size="lg" />
                <div className="fw-bold text-success" style={{ fontSize: '20px' }}>
                  {sellerMetrics.marketPosition}%
                </div>
                <small className="text-muted">Market Position</small>
              </div>
            </Col>

            {/* AI Confidence Card */}
            <Col className="d-flex">
              <div 
                className="metric-card text-center flex-fill"
                style={{
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '12px',
                  padding: '20px 12px',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  minHeight: '110px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <div 
                  className="position-absolute top-0 end-0 px-2 py-1 rounded-bottom-start text-white"
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    fontSize: '10px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  OPTIMAL
                </div>
                <FontAwesomeIcon icon={faBrain} className="text-primary mb-2" size="lg" />
                <div className="fw-bold text-primary" style={{ fontSize: '20px' }}>
                  {sellerMetrics.aiConfidence}%
                </div>
                <small className="text-muted">AI Confidence</small>
              </div>
            </Col>

            {/* Listing Readiness Card */}
            <Col className="d-flex">
              <div 
                className="metric-card text-center flex-fill"
                style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  borderRadius: '12px',
                  padding: '20px 12px',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  minHeight: '110px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <div 
                  className="position-absolute top-0 end-0 px-2 py-1 rounded-bottom-start text-white"
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    fontSize: '10px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  READY
                </div>
                <FontAwesomeIcon icon={faRocket} className="text-info mb-2" size="lg" />
                <div className="fw-bold text-info" style={{ fontSize: '20px' }}>
                  {sellerMetrics.listingReadiness}%
                </div>
                <small className="text-muted">Launch Ready</small>
              </div>
            </Col>
          </Row>
        </div>

        {/* Main Content Tabs */}
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => handleTabChange(k || 'property-details')}
          className="nav-tabs"
          fill
        >
          <Tab eventKey="property-details" title={
            <span>
              <FontAwesomeIcon icon={faHome} className="me-1" />
              Property Details
            </span>
          }>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-3">Loading property details...</div>
              </div>
            ) : sellerProfile ? (
              <Row className="g-4">
                {/* Property Information */}
                <Col md={6}>
                  <Card className="border-0 shadow-lg">
                    <Card.Header 
                      className="border-0 d-flex justify-content-between align-items-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(0, 188, 212, 0.15) 100%)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '16px 16px 0 0',
                        padding: '20px 24px',
                        border: '1px solid rgba(0, 212, 255, 0.2)'
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <div 
                          className="me-3 rounded-3 d-flex align-items-center justify-content-center" 
                          style={{ 
                            background: 'linear-gradient(135deg, #00d4ff 0%, #00bcd4 100%)',
                            width: '48px',
                            height: '48px',
                            boxShadow: '0 0 15px rgba(0, 212, 255, 0.4)',
                            border: '2px solid rgba(255, 255, 255, 0.1)'
                          }}
                        >
                          <FontAwesomeIcon 
                            icon={faHome} 
                            className="text-white"
                            style={{ 
                              fontSize: '18px',
                              filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' 
                            }}
                          />
                        </div>
                      <div>
                          <h6 
                            className="mb-1 fw-bold"
                            style={{
                              color: '#1f2937',
                              textShadow: '0 0 8px rgba(0, 212, 255, 0.3)',
                              fontSize: '1rem'
                            }}
                          >
                            Property Intelligence
                          </h6>
                          <small 
                            className="text-muted"
                            style={{ fontSize: '13px' }}
                          >
                            AI-enhanced property details
                          </small>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant={editing ? "success" : "outline-primary"} 
                        onClick={handleToggleEdit}
                        disabled={saving}
                        className="px-3"
                      >
                        <FontAwesomeIcon icon={editing ? faSave : faEdit} className="me-1" />
                        {saving ? 'Saving...' : editing ? 'Save' : 'Edit'}
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <strong>Address:</strong>
                        <div className="mt-2">

                          
                          <AddressAutocompleteInput
                            value={addressValue || sellerProfile?.property_address || ''}
                            onChange={handleAddressChange}
                            onAddressSelect={handleAddressSelect}
                            placeholder={sellerProfile?.property_address ? sellerProfile.property_address : "Enter property address..."}
                            disabled={lookupStatus === 'loading' || Boolean(sellerProfile?.property_address && !editing)}
                            locked={addressLocked}
                            onUnlock={() => {
                              setAddressLocked(false);
                              setLookupStatus('none');
                            }}
                            showClearButton={true}
                            style={{ fontSize: '1rem' }}
                          />
                          {sellerProfile?.property_address && !editing && !addressLocked && (
                            <div className="mt-2">
                              <Badge bg="success" className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                Verified Property
                              </Badge>
                            </div>
                          )}
                          
                          {/* Enhanced MLS Lookup Progress Indicator */}
                          {lookupStatus === 'loading' && (
                            <div className="mt-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                              <div className="d-flex align-items-center mb-2">
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                                <strong>Enhanced Property Analysis</strong>
                              </div>
                              <div className="progress mb-2" style={{ height: '8px' }}>
                                <div 
                                  className="progress-bar progress-bar-striped progress-bar-animated" 
                                  role="progressbar" 
                                  style={{ width: `${lookupProgress}%` }}
                                  aria-valuenow={lookupProgress} 
                                  aria-valuemin={0} 
                                  aria-valuemax={100}
                                ></div>
                              </div>
                              <small className="text-muted">{lookupStep}</small>
                            </div>
                          )}
                          
                          {/* Success/Error Status */}
                          {lookupStatus === 'success' && (
                            <div className="mt-2 alert alert-success py-2" role="alert">
                              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                              Property data retrieved and profile updated!
                            </div>
                          )}
                          
                          {lookupStatus === 'error' && (
                            <div className="mt-2 alert alert-warning py-2" role="alert">
                              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                              {lookupStep || 'Property lookup failed - you can enter details manually'}
                            </div>
                          )}
                          

                        </div>
                      </div>

                      {/* Property Details Form Fields (shown when editing) */}
                      {editing && (
                        <div className="mt-4 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                          <h6 className="text-primary mb-3">
                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                            Property Details
                          </h6>
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Bedrooms</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={formData.bedrooms || ''}
                                  onChange={(e) => setFormData({...formData, bedrooms: parseInt(e.target.value) || 0})}
                                  min="0"
                                  max="10"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Bathrooms</Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.5"
                                  value={formData.bathrooms || ''}
                                  onChange={(e) => setFormData({...formData, bathrooms: parseFloat(e.target.value) || 0})}
                                  min="0"
                                  max="10"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Square Footage</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={formData.square_footage || ''}
                                  onChange={(e) => setFormData({...formData, square_footage: parseInt(e.target.value) || 0})}
                                  min="0"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Year Built</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={formData.year_built || ''}
                                  onChange={(e) => setFormData({...formData, year_built: parseInt(e.target.value) || 0})}
                                  min="1800"
                                  max={new Date().getFullYear()}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Garage Spaces</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={formData.garage_spaces || ''}
                                  onChange={(e) => setFormData({...formData, garage_spaces: parseInt(e.target.value) || 0})}
                                  min="0"
                                  max="5"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label>Estimated Value</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={formData.estimated_value || ''}
                                  onChange={(e) => setFormData({...formData, estimated_value: parseInt(e.target.value) || 0})}
                                  min="0"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <Form.Group>
                                <Form.Check
                                  type="checkbox"
                                  label="Swimming Pool"
                                  checked={formData.pool || false}
                                  onChange={(e) => setFormData({...formData, pool: e.target.checked})}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      )}

                      {/* Property Details Display (when not editing) */}
                      {!editing && (
                        <Row className="g-3">
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Property Type:</strong>
                              <div className="text-muted">
                                {sellerProfile.property_type ? 
                                  sellerProfile.property_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
                                  'Not specified'
                                }
                              </div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Bedrooms:</strong>
                              <div className="text-muted">{sellerProfile.bedrooms || 'Not specified'}</div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Bathrooms:</strong>
                              <div className="text-muted">{sellerProfile.bathrooms || 'Not specified'}</div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Square Footage:</strong>
                              <div className="text-muted">
                                {sellerProfile.square_footage ? 
                                  `${sellerProfile.square_footage.toLocaleString()} sq ft` : 
                                  'Not specified'
                                }
                              </div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Lot Size:</strong>
                              <div className="text-muted">
                                {sellerProfile.lot_size_sqft ? 
                                  `${sellerProfile.lot_size_sqft.toLocaleString()} sq ft` : 
                                  'Not specified'
                                }
                              </div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Year Built:</strong>
                              <div className="text-muted">{sellerProfile.year_built || 'Not specified'}</div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Garage:</strong>
                              <div className="text-muted">
                                {sellerProfile.garage_spaces ? 
                                  `${sellerProfile.garage_spaces} space${sellerProfile.garage_spaces !== 1 ? 's' : ''}` : 
                                  'None'
                                }
                              </div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Pool:</strong>
                              <div className="text-muted">
                                {sellerProfile.pool ? 
                                  <Badge bg="success">Yes</Badge> : 
                                  <Badge bg="secondary">No</Badge>
                                }
                              </div>
                            </div>
                          </Col>
                          <Col md={12}>
                            <div className="mb-2">
                              <strong>Condition:</strong>
                              <div className="text-muted">
                                {sellerProfile.property_condition ? 
                                  sellerProfile.property_condition.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
                                  'Not specified'
                                }
                              </div>
                            </div>
                          </Col>
                        </Row>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                {/* Pricing Strategy */}
                <Col md={6}>
                  <Card className="border-0 shadow-lg">
                    <Card.Header 
                      className="border-0 d-flex justify-content-between align-items-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '16px 16px 0 0',
                        padding: '20px 24px',
                        border: '1px solid rgba(16, 185, 129, 0.2)'
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <div 
                          className="me-3 rounded-3 d-flex align-items-center justify-content-center" 
                          style={{ 
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            width: '48px',
                            height: '48px',
                            boxShadow: '0 0 15px rgba(16, 185, 129, 0.4)',
                            border: '2px solid rgba(255, 255, 255, 0.1)'
                          }}
                        >
                          <FontAwesomeIcon 
                            icon={faDollarSign} 
                            className="text-white"
                            style={{ 
                              fontSize: '18px',
                              filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' 
                            }}
                          />
                        </div>
                      <div>
                          <h6 
                            className="mb-1 fw-bold"
                            style={{
                              color: '#1f2937',
                              textShadow: '0 0 8px rgba(16, 185, 129, 0.3)',
                              fontSize: '1rem'
                            }}
                          >
                            Pricing Intelligence
                          </h6>
                          <small 
                            className="text-muted"
                            style={{ fontSize: '13px' }}
                          >
                            AI-powered market valuation
                          </small>
                        </div>
                      </div>
                      {sellerMetrics.estimatedValue > 0 && (
                        <Badge className="px-3 py-2 text-uppercase fw-bold" style={{ 
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          border: 'none',
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px'
                        }}>
                          <FontAwesomeIcon icon={faBrain} className="me-1" />
                          AI POWERED
                        </Badge>
                      )}
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <strong>Estimated Value:</strong>
                          {sellerMetrics.estimatedValue > 0 && (
                            <small className="text-muted">
                              <FontAwesomeIcon icon={faCheckCircle} className="me-1 text-success" />
                              From CMA Analysis
                            </small>
                          )}
                        </div>
                        <div className="h5 text-success">
                          {formatCurrency(sellerProfile.estimated_value || 0)}
                        </div>
                        {sellerMetrics.estimatedValue === 0 && (
                          <small className="text-muted">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1 text-warning" />
                            Generate CMA for AI-powered valuation
                          </small>
                        )}
                      </div>
                      <div className="mb-3">
                        <strong>Desired Price:</strong>
                        <div className="h6 text-primary">
                          {formatCurrency(sellerProfile.desired_price || 0)}
                        </div>
                      </div>
                      <div className="mb-3">
                        <strong>Minimum Price:</strong>
                        <div className="h6 text-warning">
                          {formatCurrency(sellerProfile.minimum_price || 0)}
                        </div>
                      </div>
                      <div className="mb-3">
                        <strong>Strategy:</strong>
                        <Badge bg="info" className="ms-2">
                          {sellerProfile.pricing_strategy ? 
                            sellerProfile.pricing_strategy.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
                            'Not Set'
                          }
                        </Badge>
                      </div>
                      {sellerMetrics.estimatedValue > 0 && (
                        <Alert variant="success" className="mb-0 py-2">
                          <FontAwesomeIcon icon={faThumbsUp} className="me-1" />
                          <small>
                            Pricing based on {sellerMetrics.methodology} analysis of {sellerMetrics.comparableCount} comparable properties
                          </small>
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                {/* Property Management Actions */}
                <Col md={12}>
                  <Card>
                    <Card.Header>
                      <FontAwesomeIcon icon={faCog} className="me-2 text-secondary" />
                      <strong>Property Management</strong>
                    </Card.Header>
                    <Card.Body>
                      <Row className="g-3">
                        <Col md={4}>
                          <div className="d-grid">
                            <Button 
                              variant="outline-secondary" 
                              disabled
                            >
                              <FontAwesomeIcon icon={faSearch} className="me-2" />
                              MLS Lookup
                            </Button>
                            <small className="text-muted mt-1">Coming soon</small>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="d-grid">
                            <Button 
                              variant="outline-secondary" 
                              disabled
                            >
                              <FontAwesomeIcon icon={faCamera} className="me-2" />
                              Add Photos
                            </Button>
                            <small className="text-muted mt-1">Coming soon</small>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="d-grid">
                            <Button 
                              variant={cmaReports.length > 0 ? "success" : "primary"}
                              onClick={launchSmartCMA}
                              disabled={!sellerProfile?.property_address}
                            >
                              <FontAwesomeIcon icon={cmaReports.length > 0 ? faSync : faRocket} className="me-2" />
                              {cmaReports.length > 0 ? 'Update CMA Analysis' : 'Generate Smart CMA'}
                            </Button>
                            <small className="text-muted mt-1">
                              {!sellerProfile?.property_address 
                                ? 'Enter address first'
                                : cmaReports.length > 0 
                                  ? `${cmaReports.length} report${cmaReports.length !== 1 ? 's' : ''} available`
                                  : 'Ready to analyze'
                              }
                            </small>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            ) : (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h4>Loading Seller Profile...</h4>
                <p className="text-muted">Please wait while we load the profile data.</p>
              </div>
            )}
          </Tab>

          <Tab eventKey="ai-insights" title={
            <span>
              <FontAwesomeIcon icon={faBrain} className="me-1" />
              AI Insights
            </span>
          }>
            {sellerProfile && (
              <Row className="g-4">
                {/* AI Analysis Summary */}
                <Col md={12}>
                  <Card style={{ 
                    background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(14, 165, 233, 0.05) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(0, 212, 255, 0.2)',
                    borderRadius: '16px'
                  }}>
                    <Card.Header 
                      className="d-flex justify-content-between align-items-center border-0"
                      style={{
                        background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(14, 165, 233, 0.15) 100%)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '16px 16px 0 0',
                        padding: '24px'
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <div 
                          className="me-3 rounded-3 d-flex align-items-center justify-content-center"
                          style={{
                            width: '48px',
                            height: '48px',
                            background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                            boxShadow: '0 0 15px rgba(0, 212, 255, 0.4)',
                            border: '2px solid rgba(255, 255, 255, 0.1)'
                          }}
                        >
                          <FontAwesomeIcon 
                            icon={faBrain} 
                            className="text-white" 
                            style={{ 
                              fontSize: '20px',
                              filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' 
                            }}
                          />
                        </div>
                        <strong 
                          style={{
                            color: '#1f2937',
                            textShadow: '0 0 10px rgba(0, 212, 255, 0.3)',
                            fontSize: '16px'
                          }}
                        >
                          AI-Powered Seller Intelligence
                        </strong>
                      </div>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={async () => {
                          if (!sellerProfile?.property_address) {
                            toast.error('No property address found. Enter an address in Property Intelligence first.');
                            return;
                          }
                          console.log('🔄 [REFRESH] Re-running address analysis for:', sellerProfile.property_address);
                          const fullAddress = [sellerProfile.property_address, sellerProfile.property_city, sellerProfile.property_state, sellerProfile.property_zip].filter(Boolean).join(', ');
                          await performEnhancedSellerCMAAnalysis(fullAddress, {
                            property_address: sellerProfile.property_address,
                            property_city: sellerProfile.property_city || '',
                            property_state: sellerProfile.property_state || '',
                            property_zip: sellerProfile.property_zip || ''
                          });
                        }}
                        disabled={loading || lookupStatus === 'loading'}
                      >
                        <FontAwesomeIcon icon={faSync} className="me-1" />
                        Refresh Data
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={4}>
                          <div className="text-center">
                            <h2 className="text-success">
                              {formatCurrency(sellerMetrics.estimatedValue)}
                            </h2>
                            <p className="text-muted">AI Estimated Value</p>

                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="text-center">
                            <h2 className="text-primary">
                              {sellerMetrics.aiConfidence}%
                            </h2>
                            <p className="text-muted">
                              {sellerMetrics.dataSource === 'ai_analysis' ? 'AI Confidence Score' : 'Seller Readiness Score'}
                            </p>

                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="text-center">
                            <h2 className="text-warning">
                              {sellerProfile.ai_insights?.market_timing?.predicted_days_to_sell 
                                ? `${sellerProfile.ai_insights.market_timing.predicted_days_to_sell} days`
                                : 'TBD'}
                            </h2>
                            <p className="text-muted">Predicted Time to Sell</p>

                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                {/* AI Recommendations */}
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <FontAwesomeIcon icon={faRocket} className="me-2 text-success" />
                      <strong>Pricing Strategy</strong>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <strong>Recommended List Price:</strong>
                        <div className="h4 text-success">
                          {formatCurrency(
                            sellerMetrics.estimatedValue > 0 
                            ? Math.round(sellerMetrics.estimatedValue * 1.02)
                            : sellerProfile.desired_price || 0
                          )}
                        </div>

                      </div>
                      <div className="mb-3">
                        <strong>Price Range:</strong>
                        <div>
                          {formatCurrency(
                            sellerMetrics.priceRange?.low || 
                            (sellerMetrics.estimatedValue > 0
                                ? Math.round(sellerMetrics.estimatedValue * 0.95)
                                : 0)
                          )} - {formatCurrency(
                            sellerMetrics.priceRange?.high || 
                            (sellerMetrics.estimatedValue > 0
                                ? Math.round(sellerMetrics.estimatedValue * 1.05)
                                : 0)
                          )}
                        </div>

                      </div>
                      <Alert variant="info" className="mb-0">
                        <FontAwesomeIcon icon={faThumbsUp} className="me-1" />
                        {sellerMetrics.estimatedValue > 0
                          ? `List at $${Math.round(sellerMetrics.estimatedValue * 1.02).toLocaleString()} for optimal market position. Based on ${sellerMetrics.methodology} analysis with ${sellerMetrics.comparableCount} comparable properties.`
                          : 'Generate CMA report for AI-powered pricing recommendations'
                        }

                      </Alert>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <FontAwesomeIcon icon={faCalendar} className="me-2 text-warning" />
                      <strong>Market Timing</strong>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <strong>Best Listing Window:</strong>
                        <div className="h6 text-warning">
                          {cmaReports[0]?.timing_recommendations?.recommended_timing === 'optimal_preparation' 
                            ? 'Optimal Preparation Phase' 
                            : cmaReports[0]?.timing_recommendations?.recommended_timing || 'TBD'}
                        </div>
                      </div>
                      <div className="mb-3">
                        <strong>Market Trend:</strong>
                        <Badge bg={sellerMetrics.marketTrend === 'increasing' ? 'success' : 
                                   sellerMetrics.marketTrend === 'decreasing' ? 'danger' : 'secondary'}>
                          <FontAwesomeIcon icon={
                            sellerMetrics.marketTrend === 'increasing' ? faArrowTrendUp : 
                            sellerMetrics.marketTrend === 'decreasing' ? faArrowTrendDown : faCalendar
                          } className="me-1" />
                          {sellerMetrics.marketTrend === 'decreasing' ? 'Declining' : 
                           sellerMetrics.marketTrend === 'increasing' ? 'Rising' : 'Stable'} Market
                        </Badge>
                      </div>
                      <Alert variant="warning" className="mb-0">
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        {sellerMetrics.estimatedValue > 0
                          ? `Market analysis shows ${sellerMetrics.marketTrend} trend. Current market position is "${sellerMetrics.marketPositionType}" with high confidence based on ${sellerMetrics.comparableCount} comparable sales.`
                          : 'Generate CMA report for market timing analysis'}
                      </Alert>
                    </Card.Body>
                  </Card>
                </Col>

                {/* XGBoost Analysis Results (if available) */}
                {sellerProfile.ai_insights?.analysis_type?.includes('xgboost') && (
                  <Col md={12}>
                    <Card style={{ 
                      background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(126, 34, 206, 0.05) 100%)',
                      border: '1px solid rgba(147, 51, 234, 0.2)'
                    }}>
                      <Card.Header>
                        <FontAwesomeIcon icon={faRocket} className="me-2 text-purple" />
                                                                <strong style={{ color: '#9333ea' }}>Advanced AI Analysis</strong>
                        <Badge bg="purple" className="ms-2">Machine Learning</Badge>
                      </Card.Header>
                      <Card.Body>
                        <Row className="g-3">
                          <Col md={4}>
                            <div className="text-center">
                              <div className="h4 text-purple">
                                {formatCurrency(sellerProfile.ai_insights.valuation_data?.xgboost_estimate || 0)}
                              </div>
                              <div className="text-muted">AI Valuation</div>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="text-center">
                              <div className="h4 text-info">
                                {formatCurrency(sellerProfile.ai_insights.valuation_data?.cma_estimate || 0)}
                              </div>
                              <div className="text-muted">CMA Estimate</div>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="text-center">
                              <div className="h4 text-success">
                                {formatCurrency(sellerProfile.ai_insights.valuation_data?.final_estimated_value || 0)}
                              </div>
                              <div className="text-muted">Hybrid Final Value</div>
                            </div>
                          </Col>
                        </Row>
                        
                        {/* Model Performance Metrics */}
                        {sellerProfile.ai_insights.model_performance && (
                          <div className="mt-3 p-3 rounded" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                            <h6 className="text-purple mb-3">
                              <FontAwesomeIcon icon={faChartBar} className="me-2" />
                              Model Performance
                            </h6>
                            <Row className="g-2">
                              <Col md={6}>
                                <div className="d-flex justify-content-between">
                                  <span className="text-muted">Average Accuracy:</span>
                                  <strong>{Math.round((sellerProfile.ai_insights.model_performance.average_accuracy || 0) * 100)}%</strong>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="d-flex justify-content-between">
                                  <span className="text-muted">Properties Analyzed:</span>
                                  <strong>{sellerProfile.ai_insights.model_performance.properties_analyzed || 0}</strong>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="d-flex justify-content-between">
                                  <span className="text-muted">Model Reliability:</span>
                                  <Badge bg={
                                    sellerProfile.ai_insights.model_performance.model_reliability === 'excellent' ? 'success' :
                                    sellerProfile.ai_insights.model_performance.model_reliability === 'good' ? 'primary' : 'warning'
                                  }>
                                    {(sellerProfile.ai_insights.model_performance.model_reliability || 'fair').toUpperCase()}
                                  </Badge>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="d-flex justify-content-between">
                                  <span className="text-muted">High Accuracy Count:</span>
                                  <strong>{sellerProfile.ai_insights.model_performance.high_accuracy_count || 0}</strong>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                )}

                {/* Comparable Properties Summary - Always Show Data */}
                <Col md={12}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <div>
                        <FontAwesomeIcon icon={faHome} className="me-2 text-info" />
                        <strong>Comparable Properties Analysis</strong>
                      </div>
                      {sellerMetrics.comparableMetrics && (
                        <Badge bg="success" className="ms-2">
                          <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                          Real CMA Data
                        </Badge>
                      )}
                    </Card.Header>
                    <Card.Body>
                      <Row className="g-3">
                        <Col md={3}>
                          <div className="text-center p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="h5 text-info">
                              {sellerMetrics.comparableCount || 0}
                            </div>
                            <div className="text-muted small">Total Comparables</div>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="text-center p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="h5 text-success">
                              {sellerMetrics.comparableMetrics?.recent_sales_count_12mo || sellerMetrics.comparableMetrics?.recent_sales_count || 0}
                            </div>
                            <div className="text-muted small">
                              Recent Sales (12mo)
                              {sellerMetrics.comparableMetrics?.recent_sales_count === 0 && 
                               sellerMetrics.comparableMetrics?.recent_sales_count_12mo > 0 && (
                                <div className="text-warning" style={{ fontSize: '11px' }}>
                                  (0 in last 6mo)
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="text-center p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="h5 text-warning">
                              {sellerMetrics.comparableMetrics?.average_days_on_market || 0}
                            </div>
                            <div className="text-muted small">Avg. Days on Market</div>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="text-center p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="h5 text-primary">
                              {sellerMetrics.comparableMetrics?.average_sale_price 
                                ? formatCurrency(sellerMetrics.comparableMetrics.average_sale_price)
                                : 'N/A'}
                            </div>
                            <div className="text-muted small">Avg. Sale Price</div>
                          </div>
                        </Col>
                      </Row>
                      
                      {/* Additional Market Insights */}
                      <Row className="mt-3">
                        <Col md={6}>
                          <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="text-muted">Median Sale Price:</span>
                              <strong className="text-info">
                                {sellerMetrics.comparableMetrics?.median_sale_price 
                                  ? formatCurrency(sellerMetrics.comparableMetrics.median_sale_price)
                                  : 'N/A'}
                              </strong>
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="text-muted">Price Trend:</span>
                              <Badge bg={
                                (sellerMetrics.comparableMetrics?.price_trend === 'increasing') ? 'success' : 
                                (sellerMetrics.comparableMetrics?.price_trend === 'decreasing') ? 'danger' : 'secondary'
                              }>
                                <FontAwesomeIcon icon={
                                  (sellerMetrics.comparableMetrics?.price_trend === 'increasing') ? faArrowTrendUp : 
                                  (sellerMetrics.comparableMetrics?.price_trend === 'decreasing') ? faArrowTrendDown : faCalendar
                                } className="me-1" />
                                {(sellerMetrics.comparableMetrics?.price_trend === 'decreasing') ? 'Declining' : 
                                 (sellerMetrics.comparableMetrics?.price_trend === 'increasing') ? 'Rising' : 'Unknown'}
                              </Badge>
                            </div>
                          </div>
                        </Col>
                      </Row>
                      

                    </Card.Body>
                  </Card>
                </Col>



                {/* Marketing Recommendations */}
                <Col md={12}>
                  <Card>
                    <Card.Header>
                      <FontAwesomeIcon icon={faHandsHelping} className="me-2 text-primary" />
                      <strong>Marketing Strategy Recommendations</strong>
                    </Card.Header>
                    <Card.Body>
                      {sellerMetrics.estimatedValue > 0 && sellerMetrics.comparableCount > 0 ? (
                        <div>
                          {/* Pricing Strategy Based on Market Position */}
                          <Row className="mb-4">
                            <Col md={6}>
                              <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                <h6 className="text-success mb-3">
                                  <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" />
                                  Pricing Strategy
                                </h6>
                                <div className="mb-2">
                                  <strong>Recommended List Price:</strong>
                                  <div className="h5 text-success">
                                    {formatCurrency(Math.round(sellerMetrics.estimatedValue * 1.02))}
                                  </div>
                                  <small className="text-muted">
                                    2% above AI valuation for negotiation room
                                  </small>
                                </div>
                                <Alert variant={
                                  sellerMetrics.marketPosition >= 85 ? 'success' :
                                  sellerMetrics.marketPosition >= 70 ? 'info' : 'warning'
                                } className="py-2">
                                  <FontAwesomeIcon icon={faChartLine} className="me-1" />
                                  <small>
                                    {sellerMetrics.marketPosition >= 85 
                                      ? 'Strong market position - price confidently'
                                      : sellerMetrics.marketPosition >= 70
                                      ? 'Good market position - competitive pricing recommended'
                                      : 'Consider strategic pricing to attract buyers'}
                                  </small>
                                </Alert>
                              </div>
                            </Col>
                            <Col md={6}>
                              <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                <h6 className="text-info mb-3">
                                  <FontAwesomeIcon icon={faClock} className="me-2" />
                                  Market Timing
                                </h6>
                                <div className="mb-2">
                                  <strong>Market Activity:</strong>
                                  <div>
                                    {sellerMetrics.comparableMetrics?.recent_sales_count || 0} sales in last 6 months
                                  </div>
                                </div>
                                <div className="mb-2">
                                  <strong>Average Days on Market:</strong>
                                  <div>{sellerMetrics.comparableMetrics?.average_days_on_market || 30} days</div>
                                </div>
                                <Alert variant={
                                  (sellerMetrics.comparableMetrics?.recent_sales_count || 0) > 10 ? 'success' :
                                  (sellerMetrics.comparableMetrics?.recent_sales_count || 0) > 5 ? 'info' : 'warning'
                                } className="py-2">
                                  <FontAwesomeIcon icon={faCalendar} className="me-1" />
                                  <small>
                                    {(sellerMetrics.comparableMetrics?.recent_sales_count || 0) > 10
                                      ? 'High market activity - excellent time to list'
                                      : (sellerMetrics.comparableMetrics?.recent_sales_count || 0) > 5
                                      ? 'Moderate activity - good listing conditions'
                                      : 'Lower activity - focus on standout marketing'}
                                  </small>
                                </Alert>
                              </div>
                            </Col>
                          </Row>

                          {/* Key Marketing Recommendations */}
                          <Row className="mb-4">
                            <Col md={12}>
                              <h6 className="text-primary mb-3">
                                <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                                Key Marketing Recommendations
                              </h6>
                              <div className="d-flex flex-wrap gap-2 mb-3">
                                {/* Dynamic recommendations based on data */}
                                {sellerMetrics.marketPosition >= 85 && (
                                  <Badge bg="success" className="p-2">
                                    <FontAwesomeIcon icon={faStar} className="me-1" />
                                    Premium Property - Highlight Quality
                                  </Badge>
                                )}
                                {sellerMetrics.comparableMetrics?.average_days_on_market < 30 && (
                                  <Badge bg="info" className="p-2">
                                    <FontAwesomeIcon icon={faFire} className="me-1" />
                                    Fast-Moving Market - Create Urgency
                                  </Badge>
                                )}
                                {sellerMetrics.aiConfidence >= 85 && (
                                  <Badge bg="primary" className="p-2">
                                    <FontAwesomeIcon icon={faBrain} className="me-1" />
                                    High AI Confidence - Market Aggressively
                                  </Badge>
                                )}
                                {sellerMetrics.comparableCount > 20 && (
                                  <Badge bg="secondary" className="p-2">
                                    <FontAwesomeIcon icon={faUsers} className="me-1" />
                                    Competitive Market - Stand Out
                                  </Badge>
                                )}
                              </div>
                            </Col>
                          </Row>

                          {/* Specific Marketing Tactics */}
                          <Row>
                            <Col md={6}>
                              <h6 className="text-success mb-3">
                                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                Marketing Strengths
                              </h6>
                              <ul className="list-unstyled">
                                <li className="mb-2">
                                  <FontAwesomeIcon icon={faHome} className="me-2 text-success" />
                                  <strong>Property Appeal:</strong> {sellerMetrics.comparableCount} comparable properties validate market demand
                                </li>
                                <li className="mb-2">
                                  <FontAwesomeIcon icon={faChartLine} className="me-2 text-success" />
                                  <strong>Price Confidence:</strong> {sellerMetrics.aiConfidence}% AI model confidence supports pricing
                                </li>
                                <li className="mb-2">
                                  <FontAwesomeIcon icon={faMapMarkedAlt} className="me-2 text-success" />
                                  <strong>Market Position:</strong> {
                                    sellerMetrics.marketPosition >= 85 ? 'Premium' :
                                    sellerMetrics.marketPosition >= 70 ? 'Above Average' : 'Competitive'
                                  } positioning in local market
                                </li>
                              </ul>
                            </Col>
                            <Col md={6}>
                              <h6 className="text-info mb-3">
                                <FontAwesomeIcon icon={faBullhorn} className="me-2" />
                                Marketing Tactics
                              </h6>
                              <ul className="list-unstyled">
                                <li className="mb-2">
                                  <FontAwesomeIcon icon={faCamera} className="me-2 text-info" />
                                  <strong>Professional Photography:</strong> Essential with {sellerMetrics.comparableCount} competing listings
                                </li>
                                <li className="mb-2">
                                  <FontAwesomeIcon icon={faVideo} className="me-2 text-info" />
                                  <strong>Virtual Tour:</strong> Stand out in {
                                    (sellerMetrics.comparableMetrics?.average_days_on_market || 30) < 30 ? 'fast-moving' : 'competitive'
                                  } market
                                </li>
                                <li className="mb-2">
                                  <FontAwesomeIcon icon={faBullseye} className="me-2 text-info" />
                                  <strong>Targeted Marketing:</strong> Focus on buyers seeking {
                                    sellerMetrics.estimatedValue > 500000 ? 'premium' :
                                    sellerMetrics.estimatedValue > 300000 ? 'mid-range' : 'affordable'
                                  } homes
                                </li>
                              </ul>
                            </Col>
                          </Row>

                          {/* Call to Action */}
                          <Alert variant="success" className="mt-3 mb-0">
                            <FontAwesomeIcon icon={faRocket} className="me-2" />
                            <strong>Next Steps:</strong> Schedule professional photography and create compelling listing description highlighting your property's {sellerMetrics.marketPosition >= 85 ? 'premium features' : 'competitive advantages'}.
                          </Alert>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="text-muted mb-3" size="2x" />
                          <p className="text-muted">Generate a CMA report to unlock AI-powered marketing recommendations</p>
                          <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => launchSmartCMA()}
                          >
                            <FontAwesomeIcon icon={faFileLines} className="me-2" />
                            Generate CMA Report
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Tab>

          <Tab eventKey="cma-reports" title={
            <span>
              <FontAwesomeIcon icon={faChartLine} className="me-1" />
              CMA Reports
            </span>
          }>
            <Row className="g-4">
              <Col xs={12}>
                <div 
                  className="d-flex justify-content-between align-items-center mb-4 p-4 rounded-3"
                  style={{
                    background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)',
                    border: '1px solid rgba(147, 51, 234, 0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div 
                      className="me-3 rounded-3 d-flex align-items-center justify-content-center" 
                      style={{ 
                        background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
                        width: '64px',
                        height: '64px',
                        boxShadow: '0 0 20px rgba(147, 51, 234, 0.4)',
                        border: '2px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <FontAwesomeIcon 
                        icon={faChartLine} 
                        className="text-white" 
                        style={{ 
                          fontSize: '24px',
                          filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' 
                        }} 
                      />
                    </div>
                  <div>
                      <h5 
                        className="mb-2 fw-bold"
                        style={{
                          color: '#1f2937',
                          textShadow: '0 0 10px rgba(147, 51, 234, 0.3)',
                          fontSize: '1.3rem'
                        }}
                      >
                        AI Market Analysis Reports
                      </h5>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        Neural network powered CMA insights with advanced AI valuations
                      {cmaReports.length > 0 && (
                        <span className="ms-2">
                            <span
                              className="px-2 py-1 rounded-pill"
                              style={{ 
                                background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
                                fontSize: '11px',
                                color: 'white',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}
                            >
                              {cmaReports.length} report{cmaReports.length !== 1 ? 's' : ''} generated
                            </span>
                        </span>
                      )}
                    </p>
                  </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button 
                      variant="outline-secondary" 
                      onClick={loadCMAReports}
                      disabled={loading || isLoadingCMAReports}
                      className="px-3"
                    >
                      <FontAwesomeIcon icon={isLoadingCMAReports ? faSpinner : faSync} className={`me-1 ${isLoadingCMAReports ? 'fa-spin' : ''}`} />
                      {isLoadingCMAReports ? 'Loading...' : 'Refresh'}
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={launchSmartCMA}
                      disabled={!sellerProfile || loading}
                      className="px-3"
                    >
                      <FontAwesomeIcon icon={faBrain} className="me-1" />
                      Generate AI CMA
                    </Button>
                  </div>
                </div>
              </Col>

              {/* CMA Reports List */}
              <Col xs={12}>
                {cmaReports.length > 0 ? (
                  <Row className="g-3">
                    {cmaReports.map((report, index) => (
                      <Col md={6} lg={4} key={index}>
                        <Card className="h-100" style={{ 
                          borderRadius: '12px',
                          border: '1px solid rgba(0,0,0,0.1)',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer'
                        }}>
                          <Card.Header className="d-flex justify-content-between align-items-center bg-light">
                            <div>
                              <h6 className="mb-0">
                                {report.report_title || `CMA Report - ${report.report_purpose?.replace('_', ' ').toUpperCase() || 'Market Analysis'}`}
                              </h6>
                              <small className="text-muted">
                                {new Date(report.created_on).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </small>
                            </div>
                            <Badge bg={
                              (report.pricing_recommendations?.confidence_score || report.confidence_score || 0) >= 0.8 ? 'success' : 'warning'
                            }>
                              {(report.pricing_recommendations?.confidence_score || report.confidence_score || 0) >= 0.8 ? 'High Confidence' : 'Moderate Confidence'}
                            </Badge>
                          </Card.Header>
                          <Card.Body>
                            <div className="mb-3">
                              <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Estimated Value:</span>
                                <strong className="text-success">
                                  {formatCurrency(
                                    report.pricing_recommendations?.estimated_value || 
                                    report.pricing_recommendations?.final_estimated_value ||
                                    report.predicted_value || 0
                                  )}
                                </strong>
                              </div>
                              <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Confidence:</span>
                                <span className="fw-bold">
                                  {Math.round(
                                    (report.pricing_recommendations?.confidence_score || report.confidence_score || 0) * 
                                    ((report.confidence_score || 0) > 1 ? 1 : 100)
                                  )}%
                                </span>
                              </div>
                              <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Comparables:</span>
                                <span>
                                  {report.marketing_recommendations?.comparable_count || 
                                   report.marketing_recommendations?.competitive_advantages?.similar_properties || 
                                   'N/A'} properties
                                </span>
                              </div>
                              <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Analysis Type:</span>
                                <Badge bg="info" className="text-uppercase" style={{ fontSize: '0.7rem' }}>
                                  {report.pricing_recommendations?.methodology?.includes('xgboost') ? 'AI+XGBoost' : 
                                   report.marketing_recommendations?.analysis_type?.includes('xgboost') ? 'AI Enhanced' : 'Standard'}
                                </Badge>
                              </div>
                              {report.pricing_recommendations?.price_range && (
                                <div className="mt-2 p-2 bg-light rounded">
                                  <small className="text-muted d-block">Price Range:</small>
                                  <small className="fw-bold">
                                    {formatCurrency(report.pricing_recommendations.price_range.low)} - {formatCurrency(report.pricing_recommendations.price_range.high)}
                                  </small>
                                </div>
                              )}
                            </div>
                            <div className="d-grid gap-2">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                  // Open Step 2-4 workflow in new window for detailed review
                                  const host = window.location.hostname;
                                  const base = host === 'localhost'
                                    ? `http://localhost:${window.location.port || '3000'}`
                                    : `${window.location.protocol}//${host}`;
                                  const workflowUrl = `${base}/smart-cma-workflow?reportId=${report.report_id}&step=2`;
                                  window.open(workflowUrl, '_blank');
                                }}
                              >
                                <FontAwesomeIcon icon={faList} className="me-1" />
                                Review & Edit Report
                              </Button>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="flex-grow-1"
                                  onClick={() => viewCMAReport(report.report_id)}
                                >
                                  <FontAwesomeIcon icon={faEye} className="me-1" />
                                  View Report
                                </Button>
                                <Button
                                  variant="success"
                                  size="sm"
                                  className="flex-grow-1"
                                  onClick={() => {
                                    const reportUrl = (() => {
                                      const host = window.location.hostname;
                                      let agentId = userInfo?.id || '';
                                      if (!agentId) {
                                        try {
                                          const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token') || '';
                                          if (token) agentId = JSON.parse(atob(token.split('.')[1])).id || '';
                                        } catch (e) {}
                                      }
                                      const base = host.includes('dev.') || host === 'localhost'
                                        ? 'https://dev.aisearchportal.com'
                                        : 'https://aisearchportal.com';
                                      return `${base}/cma-report/${report.report_id}${agentId ? `?agentId=${agentId}` : ''}`;
                                    })();
                                    navigator.clipboard.writeText(reportUrl);
                                    toast.success('Report link copied! Share this link with your seller.');
                                  }}
                                >
                                  <FontAwesomeIcon icon={faHandshake} className="me-1" />
                                  Share with Seller
                                </Button>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleArchiveClick(report.report_id, `CMA Report - ${new Date(report.created_on).toLocaleDateString()}`)}
                                  title="Archive this report"
                                >
                                  <FontAwesomeIcon icon={faArchive} />
                                </Button>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Card className="text-center py-5">
                    <Card.Body>
                      <FontAwesomeIcon icon={faChartLine} size="3x" className="text-muted mb-3" />
                      <h5>No CMA Reports Generated</h5>
                      <p className="text-muted mb-4">
                        Generate your first Comparative Market Analysis report to get professional property valuation insights.
                      </p>
                      <Button 
                        variant="primary" 
                        size="lg"
                        onClick={launchSmartCMA}
                        disabled={!sellerProfile || loading}
                      >
                        <FontAwesomeIcon icon={faRocket} className="me-2" />
                        Generate Your First CMA Report
                      </Button>
                      <div className="mt-4">
                        <small className="text-muted">
                          <FontAwesomeIcon icon={faBrain} className="me-1" />
                          Powered by AI and 57-feature property analysis
                        </small>
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="ai-chat" title={
            <span>
              <FontAwesomeIcon icon={faMessage} className="me-1" />
              AI Chat
            </span>
          }>
            <div className="ai-chat-tab">
              {!showEmbeddedChat && !showProfileBuilder && (
                <div className="text-center py-5">
                  <div className="position-relative mb-4">
                    <div className="neural-icon-wrapper mx-auto mb-3">
                    <FontAwesomeIcon icon={faRobot} className="neural-icon" />
                  </div>
                    <div className="position-absolute top-0 start-50 translate-middle-x" 
                         style={{ marginTop: '-10px', marginLeft: '30px' }}>
                      <div className="bg-success rounded-circle d-flex align-items-center justify-content-center" 
                           style={{ width: '16px', height: '16px' }}>
                        <div className="bg-white rounded-circle" style={{ width: '6px', height: '6px' }}></div>
                      </div>
                    </div>
                  </div>
                  <h4 className="mb-3 fw-bold">Seller AI Intelligence</h4>
                  <p className="text-muted mb-4" style={{ maxWidth: '500px', margin: '0 auto' }}>
                    Engage with our neural assistant to build a comprehensive seller profile through natural conversation and AI-guided workflows
                  </p>
                  <div className="d-flex justify-content-center gap-3 mb-4">
                    <div className="text-center">
                      <div className="text-purple fw-bold h6">57</div>
                      <div className="small text-muted">Data Points</div>
                    </div>
                    <div className="text-center">
                      <div className="text-purple fw-bold h6">AI</div>
                      <div className="small text-muted">Powered</div>
                    </div>
                    <div className="text-center">
                      <div className="text-purple fw-bold h6">15min</div>
                      <div className="small text-muted">Average</div>
                    </div>
                  </div>
                  <Button 
                    variant="primary" 
                    size="lg"
                    onClick={() => setShowProfileBuilder(true)}
                    className="px-5 py-3 fw-bold text-uppercase"
                    style={{ 
                      borderRadius: '15px',
                      letterSpacing: '1px',
                      fontSize: '0.9rem'
                    }}
                  >
                    <FontAwesomeIcon icon={faBrain} className="me-2" />
                    Launch AI Assistant
                  </Button>
                </div>
              )}
              
              {showProfileBuilder && renderProfileBuilderIntro()}
              {showEmbeddedChat && renderChatInterface()}
            </div>
          </Tab>

          <Tab eventKey="market-activity" title={
            <span>
              <FontAwesomeIcon icon={faUsers} className="me-1" />
              Market Activity
            </span>
          }>
            {/* Market Activity Content will be added here */}
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faUsers} size="3x" className="text-warning mb-3" />
              <h4>Market Activity</h4>
              <p className="text-muted">Showings, inquiries, and market feedback</p>
            </div>
          </Tab>

          <Tab eventKey="profile" title={
            <span>
              <FontAwesomeIcon icon={faUser} className="me-1" />
              Profile
            </span>
          }>
            {/* Comprehensive Seller Profile Editing Form */}
            <Row className="g-4">
              <Col xs={12}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="mb-1">
                      <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                      Seller Profile Information
                    </h5>
                    <p className="text-muted mb-0">View and edit all seller information and preferences</p>
                  </div>
                  <div>
                    {editing ? (
                      <>
                        <Button 
                          variant="outline-secondary" 
                          className="me-2"
                          onClick={() => setEditing(false)}
                        >
                          <FontAwesomeIcon icon={faTimes} className="me-1" />
                          Cancel
                        </Button>
                        <Button 
                          variant="success"
                          onClick={handleSaveEditing}
                          disabled={saving}
                        >
                          <FontAwesomeIcon icon={faSave} className="me-1" />
                          {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </>
                    ) : (
                      <Button 
                        variant="primary"
                        onClick={() => setEditing(true)}
                      >
                        <FontAwesomeIcon icon={faEdit} className="me-1" />
                        Edit Profile
                      </Button>
                    )}
                  </div>
                </div>
              </Col>

              {/* Contact Information Section */}
              <Col xs={12}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">
                      <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                      Contact Information
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>First Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData?.lead_first_name || ''}
                            onChange={(e) => setFormData(prev => ({...prev, lead_first_name: e.target.value}))}
                            disabled={!editing}
                            placeholder="Enter first name"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Last Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData?.lead_last_name || ''}
                            onChange={(e) => setFormData(prev => ({...prev, lead_last_name: e.target.value}))}
                            disabled={!editing}
                            placeholder="Enter last name"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            value={formData?.lead_email || ''}
                            onChange={(e) => setFormData(prev => ({...prev, lead_email: e.target.value}))}
                            disabled={!editing}
                            placeholder="Enter email address"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Phone</Form.Label>
                          <Form.Control
                            type="tel"
                            value={formData?.lead_phone || ''}
                            onChange={(e) => setFormData(prev => ({...prev, lead_phone: e.target.value}))}
                            disabled={!editing}
                            placeholder="Enter phone number"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              {/* Property Information Section */}
              <Col xs={12}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">
                      <FontAwesomeIcon icon={faHome} className="me-2 text-success" />
                      Property Information
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>Property Address</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData?.property_address || ''}
                            onChange={(e) => setFormData(prev => ({...prev, property_address: e.target.value}))}
                            disabled={!editing}
                            placeholder="Enter property address"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>City</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData?.property_city || ''}
                            onChange={(e) => setFormData(prev => ({...prev, property_city: e.target.value}))}
                            disabled={!editing}
                            placeholder="Enter city"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>State</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData?.property_state || ''}
                            onChange={(e) => setFormData(prev => ({...prev, property_state: e.target.value}))}
                            disabled={!editing}
                            placeholder="Enter state"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>ZIP Code</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData?.property_zip || ''}
                            onChange={(e) => setFormData(prev => ({...prev, property_zip: e.target.value}))}
                            disabled={!editing}
                            placeholder="Enter ZIP code"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Property Type</Form.Label>
                          <Form.Select
                            value={formData?.property_type || ''}
                            onChange={(e) => setFormData(prev => ({...prev, property_type: e.target.value}))}
                            disabled={!editing}
                          >
                            <option value="">Select property type</option>
                            <option value="single_family">Single Family Home</option>
                            <option value="townhouse">Townhouse</option>
                            <option value="condo">Condominium</option>
                            <option value="multi_family">Multi-Family</option>
                            <option value="land">Land/Lot</option>
                            <option value="commercial">Commercial</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Year Built</Form.Label>
                          <Form.Control
                            type="number"
                            value={formData?.year_built || ''}
                            onChange={(e) => setFormData(prev => ({...prev, year_built: parseInt(e.target.value) || 0}))}
                            disabled={!editing}
                            placeholder="Enter year built"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Bedrooms</Form.Label>
                          <Form.Control
                            type="number"
                            value={formData?.bedrooms || ''}
                            onChange={(e) => setFormData(prev => ({...prev, bedrooms: parseInt(e.target.value) || 0}))}
                            disabled={!editing}
                            placeholder="Number of bedrooms"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Bathrooms</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.5"
                            value={formData?.bathrooms || ''}
                            onChange={(e) => setFormData(prev => ({...prev, bathrooms: parseFloat(e.target.value) || 0}))}
                            disabled={!editing}
                            placeholder="Number of bathrooms"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Square Footage</Form.Label>
                          <Form.Control
                            type="number"
                            value={formData?.square_footage || ''}
                            onChange={(e) => setFormData(prev => ({...prev, square_footage: parseInt(e.target.value) || 0}))}
                            disabled={!editing}
                            placeholder="Total square footage"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Garage Spaces</Form.Label>
                          <Form.Control
                            type="number"
                            value={formData?.garage_spaces || ''}
                            onChange={(e) => setFormData(prev => ({...prev, garage_spaces: parseInt(e.target.value) || 0}))}
                            disabled={!editing}
                            placeholder="Number of garage spaces"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Lot Size (sq ft)</Form.Label>
                          <Form.Control
                            type="number"
                            value={formData?.lot_size_sqft || ''}
                            onChange={(e) => setFormData(prev => ({...prev, lot_size_sqft: parseInt(e.target.value) || 0}))}
                            disabled={!editing}
                            placeholder="Lot size in square feet"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Pool</Form.Label>
                          <Form.Check
                            type="checkbox"
                            label="Property has a pool"
                            checked={formData?.pool || false}
                            onChange={(e) => setFormData(prev => ({...prev, pool: e.target.checked}))}
                            disabled={!editing}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              {/* Selling Information Section */}
              <Col xs={12}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">
                      <FontAwesomeIcon icon={faHandshake} className="me-2 text-warning" />
                      Selling Information
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Selling Timeframe</Form.Label>
                          <Form.Select
                            value={formData?.selling_timeframe || ''}
                            onChange={(e) => setFormData(prev => ({...prev, selling_timeframe: e.target.value}))}
                            disabled={!editing}
                          >
                            <option value="">Select timeframe</option>
                            <option value="within_1_month">Within 1 month</option>
                            <option value="within_3_months">Within 3 months</option>
                            <option value="within_6_months">Within 6 months</option>
                            <option value="within_1_year">Within 1 year</option>
                            <option value="flexible">Flexible</option>
                            <option value="not_specified">Not specified</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Motivation Level</Form.Label>
                          <Form.Select
                            value={formData?.motivation_level || ''}
                            onChange={(e) => setFormData(prev => ({...prev, motivation_level: e.target.value}))}
                            disabled={!editing}
                          >
                            <option value="">Select motivation level</option>
                            <option value="urgent">Urgent - Must sell</option>
                            <option value="high">High - Want to sell</option>
                            <option value="medium">Medium - Considering selling</option>
                            <option value="low">Low - Just exploring</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>Reason for Selling</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={formData?.reason_for_selling || ''}
                            onChange={(e) => setFormData(prev => ({...prev, reason_for_selling: e.target.value}))}
                            disabled={!editing}
                            placeholder="Enter reason for selling"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Property Condition</Form.Label>
                          <Form.Select
                            value={formData?.property_condition || ''}
                            onChange={(e) => setFormData(prev => ({...prev, property_condition: e.target.value}))}
                            disabled={!editing}
                          >
                            <option value="">Select condition</option>
                            <option value="excellent">Excellent</option>
                            <option value="good">Good</option>
                            <option value="fair">Fair</option>
                            <option value="needs_work">Needs Work</option>
                            <option value="fixer_upper">Fixer Upper</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Occupancy Status</Form.Label>
                          <Form.Select
                            value={formData?.occupancy_status || ''}
                            onChange={(e) => setFormData(prev => ({...prev, occupancy_status: e.target.value}))}
                            disabled={!editing}
                          >
                            <option value="">Select occupancy</option>
                            <option value="owner_occupied">Owner Occupied</option>
                            <option value="tenant_occupied">Tenant Occupied</option>
                            <option value="vacant">Vacant</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              {/* Pricing Information Section */}
              <Col xs={12}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">
                      <FontAwesomeIcon icon={faDollarSign} className="me-2 text-success" />
                      Pricing Information
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Estimated Value</Form.Label>
                          <Form.Control
                            type="number"
                            value={formData?.estimated_value || ''}
                            onChange={(e) => setFormData(prev => ({...prev, estimated_value: parseInt(e.target.value) || 0}))}
                            disabled={!editing}
                            placeholder="Enter estimated value"
                          />
                          <Form.Text className="text-muted">
                            Current market estimate
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Desired Price</Form.Label>
                          <Form.Control
                            type="number"
                            value={formData?.desired_price || ''}
                            onChange={(e) => setFormData(prev => ({...prev, desired_price: parseInt(e.target.value) || 0}))}
                            disabled={!editing}
                            placeholder="Enter desired price"
                          />
                          <Form.Text className="text-muted">
                            What seller hopes to get
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Minimum Price</Form.Label>
                          <Form.Control
                            type="number"
                            value={formData?.minimum_price || ''}
                            onChange={(e) => setFormData(prev => ({...prev, minimum_price: parseInt(e.target.value) || 0}))}
                            disabled={!editing}
                            placeholder="Enter minimum price"
                          />
                          <Form.Text className="text-muted">
                            Lowest acceptable price
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Pricing Strategy</Form.Label>
                          <Form.Select
                            value={formData?.pricing_strategy || ''}
                            onChange={(e) => setFormData(prev => ({...prev, pricing_strategy: e.target.value}))}
                            disabled={!editing}
                          >
                            <option value="">Select strategy</option>
                            <option value="aggressive">Aggressive - Price to sell quickly</option>
                            <option value="market_value">Market Value - Price at market rate</option>
                            <option value="premium">Premium - Price above market</option>
                            <option value="flexible">Flexible - Open to negotiation</option>
                            <option value="not_specified">Not specified</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Recent Improvements</Form.Label>
                          <Form.Check
                            type="checkbox"
                            label="Property has recent improvements"
                            checked={formData?.recent_improvements || false}
                            onChange={(e) => setFormData(prev => ({...prev, recent_improvements: e.target.checked}))}
                            disabled={!editing}
                          />
                        </Form.Group>
                      </Col>
                      {formData?.recent_improvements && (
                        <>
                          <Col md={8}>
                            <Form.Group>
                              <Form.Label>Improvements Description</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={formData?.improvements_description || ''}
                                onChange={(e) => setFormData(prev => ({...prev, improvements_description: e.target.value}))}
                                disabled={!editing}
                                placeholder="Describe the improvements made"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>Improvements Cost</Form.Label>
                              <Form.Control
                                type="number"
                                value={formData?.improvements_cost || ''}
                                onChange={(e) => setFormData(prev => ({...prev, improvements_cost: parseInt(e.target.value) || 0}))}
                                disabled={!editing}
                                placeholder="Total cost"
                              />
                            </Form.Group>
                          </Col>
                        </>
                      )}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              {/* Additional Information Section */}
              <Col xs={12}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">
                      <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-secondary" />
                      Additional Information
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Seller Stage</Form.Label>
                          <Form.Select
                            value={formData?.seller_stage || ''}
                            onChange={(e) => setFormData(prev => ({...prev, seller_stage: e.target.value}))}
                            disabled={!editing}
                          >
                            <option value="">Select stage</option>
                            <option value="considering">Considering</option>
                            <option value="researching">Researching</option>
                            <option value="preparing">Preparing</option>
                            <option value="ready_to_list">Ready to List</option>
                            <option value="listing">Listing</option>
                            <option value="showing">Showing</option>
                            <option value="reviewing_offers">Reviewing Offers</option>
                            <option value="under_contract">Under Contract</option>
                            <option value="closed">Closed</option>
                            <option value="paused">Paused</option>
                            <option value="lost">Lost</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Priority Level</Form.Label>
                          <Form.Select
                            value={formData?.priority_level || ''}
                            onChange={(e) => setFormData(prev => ({...prev, priority_level: e.target.value}))}
                            disabled={!editing}
                          >
                            <option value="">Select priority</option>
                            <option value="high">High Priority</option>
                            <option value="medium">Medium Priority</option>
                            <option value="low">Low Priority</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Check
                            type="checkbox"
                            label="Seller is also looking to buy a property"
                            checked={formData?.is_also_buyer || false}
                            onChange={(e) => setFormData(prev => ({...prev, is_also_buyer: e.target.checked}))}
                            disabled={!editing}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              {/* Data Status Summary */}
              <Col xs={12}>
                <Card className="border-info">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">
                      <FontAwesomeIcon icon={faChartBar} className="me-2 text-info" />
                      Profile Completeness
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="d-flex justify-content-between mb-2">
                          <span>Contact Information:</span>
                          <Badge bg={
                            (sellerProfile?.lead_first_name && sellerProfile?.lead_last_name && sellerProfile?.lead_phone) 
                            ? 'success' : 'warning'
                          }>
                            {(sellerProfile?.lead_first_name && sellerProfile?.lead_last_name && sellerProfile?.lead_phone) 
                            ? 'Complete' : 'Incomplete'}
                          </Badge>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>Property Details:</span>
                          <Badge bg={
                            (sellerProfile?.property_address && sellerProfile?.bedrooms && sellerProfile?.bathrooms) 
                            ? 'success' : 'warning'
                          }>
                            {(sellerProfile?.property_address && sellerProfile?.bedrooms && sellerProfile?.bathrooms) 
                            ? 'Complete' : 'Incomplete'}
                          </Badge>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="d-flex justify-content-between mb-2">
                          <span>Selling Information:</span>
                          <Badge bg={
                            (sellerProfile?.selling_timeframe && sellerProfile?.motivation_level) 
                            ? 'success' : 'warning'
                          }>
                            {(sellerProfile?.selling_timeframe && sellerProfile?.motivation_level) 
                            ? 'Complete' : 'Incomplete'}
                          </Badge>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>Pricing Information:</span>
                          <Badge bg={
                            (sellerProfile?.estimated_value || sellerProfile?.desired_price) 
                            ? 'success' : 'warning'
                          }>
                            {(sellerProfile?.estimated_value || sellerProfile?.desired_price) 
                            ? 'Complete' : 'Incomplete'}
                          </Badge>
                        </div>
                      </Col>
                    </Row>
                    
                    {process.env.NODE_ENV === 'development' && (
                      <div className="mt-3 pt-3 border-top">
                        <small className="text-muted">
                          <strong>Debug Info:</strong><br />
                          Profile ID: {sellerProfile?.id || 'Not created'}<br />
                          Last Updated: {sellerProfile?.updated_on || 'Never'}<br />
                          Seller Score: {sellerProfile?.seller_score || 0}
                        </small>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>
        </Tabs>
      </div>

      {/* Enhanced Archive Confirmation Modal */}
      <Modal 
        show={showArchiveModal} 
        onHide={cancelArchive}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="border-0 pb-0">
          <Modal.Title className="d-flex align-items-center">
            <div 
              className="me-3 d-flex align-items-center justify-content-center"
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                color: 'white'
              }}
            >
              <FontAwesomeIcon icon={faArchive} size="lg" />
            </div>
            <div>
              <h5 className="mb-1">Archive CMA Report</h5>
              <small className="text-muted">This action cannot be undone</small>
            </div>
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="pt-2">
          <div className="text-center mb-4">
            <p className="mb-3">
              Are you sure you want to archive <strong>{reportTitleToArchive}</strong>?
            </p>
            
            <div 
              className="p-3 rounded-3 mb-3"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.1) 0%, rgba(238, 90, 36, 0.1) 100%)',
                border: '1px solid rgba(255, 107, 107, 0.2)'
              }}
            >
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FontAwesomeIcon icon={faArchive} className="me-2 text-warning" />
                <strong className="text-dark">What happens when you archive:</strong>
              </div>
              <ul className="list-unstyled mb-0 text-start">
                <li className="mb-1">
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" size="sm" />
                  Report will be moved to archived status
                </li>
                <li className="mb-1">
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" size="sm" />
                  Data will be preserved for audit purposes
                </li>
                <li className="mb-1">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" size="sm" />
                  Report will no longer appear in active reports
                </li>
                <li>
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" size="sm" />
                  This action cannot be reversed
                </li>
              </ul>
            </div>
          </div>
        </Modal.Body>
        
        <Modal.Footer className="border-0 pt-0">
          <Button 
            variant="outline-secondary" 
            onClick={cancelArchive}
            className="px-4"
          >
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmArchive}
            className="px-4"
            style={{
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
              border: 'none',
              boxShadow: '0 4px 15px rgba(255, 107, 107, 0.3)'
            }}
          >
            <FontAwesomeIcon icon={faArchive} className="me-2" />
            Archive Report
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default SellerProfilePage; 